import { useMusicBeatGrid } from "../lib/musicBeatGrid";
import { timeChoreography } from "../lib/beatSync";
import { trackPublishAttempt } from "../lib/metaIntentTracking";
import { PostFormatToggle } from "./PostFormatToggle";
import { doc, getDoc, setDoc } from "@firebase/firestore";
import { ref, uploadBytesResumable } from "firebase/storage";
import JSZip from "jszip";
import { compact } from "lodash";
import {
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  Music,
  ChevronRight,
  Copy,
  Crown,
  Download,
  Images,
  Info,
  Loader2,
  Menu,
  Pencil,
  Plus,
  Send,
  Share2,
  Play,
  Sparkles,
  Video,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../contexts/AuthContext";
import { useStripe } from "../contexts/StripeContext";
import {
  cn,
  convertHeicToPng,
  createNewDraftForWorkspace,
  formatDate,
  getAllValidConnectionsForUser,
  getPostForUser,
  groupConnectionsByPlatform,
} from "../lib/utils";
import { getEffectivePlatformDescription } from "../lib/platformCaptions";
import { beginWorkspaceOAuth } from "../lib/workspaceApi";
import { PlatformConnection, PlatformType } from "../models/Platform";
import { BrandKit } from "../models/BrandKit";
import {
  AnyOverlay,
  ConnectionPost,
  FirstCommentOverrides,
  PlatformSpecificSettings,
  PinterestSettings,
  Post,
  MediaItem as PostMediaItem,
  VideoSettingsOverrides,
  isVideoPost as isVideoPostEnabled,
} from "../models/Post";
import {
  db,
  getSignedUrl,
  isFirebaseNetworkError,
  isFirebaseStorageUrl,
  storage,
} from "../services/Firebase";
import { derivePostStatus } from "../utils/postStatus";
import { buildScheduleByDate, findConflicts } from "../utils/scheduleDensity";
import { useScheduledPosts } from "../hooks/useScheduledPosts";
import { SchedulePicker } from "./SchedulePicker";
import { useDashboardSidebar } from "./DashboardLayout";
import { useUndoHistory } from "../hooks/useUndoHistory";
import { MediaEditor } from "./MediaEditor";
import { ImageCompositeEditor } from "./ImageCompositeEditor";
import { MediaFilmstrip } from "./MediaFilmstrip";
import { ScenePreviewCanvasWrapper } from "./ScenePreviewCanvasWrapper";
import { CarouselVideoPreview } from "../remotion/CarouselVideoPreview";
import { SlideData } from "../remotion/compositions/CarouselVideo";
import { VideoChoreography } from "../models/ChoreographySpec";
import { PostSettingsPanel } from "./PostSettingsPanel";
import { ConnectionsLoadStatus, PublishModal } from "./PublishModal";
import {
  FIRST_COMMENT_PLATFORMS,
  initialConnectionSettings,
} from "../lib/publishDefaults";
import {
  ALLOWED_TYPE_PATTERNS,
  MAX_FILE_SIZE,
  MAX_NUM_FILES,
  MediaUploadSection,
  UploadItem,
} from "./MediaUploadSection";
import {
  defaultSettings,
  getPlatformConfig,
} from "./PlatformSettings";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useToast } from "./ui/use-toast";
import { useUpgradePage } from "../hooks/useUpgradePage";
import { WatermarkConfirmDialog } from "./WatermarkConfirmDialog";
import { AlreadyPublishedDialog, AlreadyPublishedNotice } from "./AlreadyPublishedDialog";
import { EXPORT_GATE_ENABLED } from "../constants/features";
import posthog from "posthog-js";
import { isSvgOverlay, isTextOverlay } from "../lib/utils";
import { platformSupportsVideoPosts } from "../lib/validate";
import { resolveMusicUrl } from "../lib/musicUrl";
import { setEditorFlush } from "../lib/editorFlush";
import { readReturnToPostTask, stashReturnToPost } from "../lib/returnToPost";
import { getConnectionErrorMessage } from "../utils/connectionErrorMessages";
import { useIsMobile } from "../hooks/useIsMobile";
import { postConverter } from "../services/converters";
import { MediaPlayer } from "./MediaPlayer";
import { UploadedVideoRenditionPreview } from "./UploadedVideoRenditionPreview";
import { VideoAudioSettings } from "./VideoAudioSettings";
import { prefetchMusicLibrary } from "./MusicPicker";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Separator } from "./ui/separator";

function triggerBrowserDownload(
  url: string,
  filename: string,
  options: { revokeObjectUrl?: boolean } = {}
) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();

  window.setTimeout(() => {
    a.remove();
    if (options.revokeObjectUrl) {
      URL.revokeObjectURL(url);
    }
  }, 1000);
}

async function fetchBlobForDownload(url: string, filename: string) {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to download ${filename}: ${res.status} ${res.statusText}`);
  }
  return res.blob();
}

/** Fetch a signed URL as a blob and trigger a browser download with the given filename. */
async function downloadBlob(url: string, filename: string) {
  const blob = await fetchBlobForDownload(url, filename);
  const objectUrl = URL.createObjectURL(blob);
  triggerBrowserDownload(objectUrl, filename, { revokeObjectUrl: true });
}

/** Derive a file extension from a storage path (e.g. "exports/foo/bar.jpg" → "jpg"). */
function extFromPath(path: string): string {
  const dot = path.lastIndexOf(".");
  if (dot === -1) return "jpg";
  return path.substring(dot + 1).toLowerCase();
}

async function downloadExportResult(exportResult: any, exportTitle: string) {
  if (!exportResult) return;

  if (exportResult.videoUrl) {
    triggerBrowserDownload(exportResult.videoUrl, `${exportTitle}.mp4`);
    return;
  }

  if (exportResult.pdfDownloadUrl) {
    await downloadBlob(exportResult.pdfDownloadUrl, `${exportTitle}.pdf`);
    return;
  }

  const items = exportResult.items ?? [];
  if (items.length > 1 && items.every((item: any) => item.type === "image")) {
    const zip = new JSZip();
    await Promise.all(
      items.map(async (item: any, idx: number) => {
        const ext = extFromPath(item.processedPath);
        const blob = await fetchBlobForDownload(
          item.downloadUrl,
          `${exportTitle}_${idx + 1}.${ext}`
        );
        zip.file(`${exportTitle}_${idx + 1}.${ext}`, blob);
      })
    );
    const zipBlob = await zip.generateAsync({ type: "blob" });
    const objectUrl = URL.createObjectURL(zipBlob);
    triggerBrowserDownload(objectUrl, `${exportTitle}.zip`, {
      revokeObjectUrl: true,
    });
    return;
  }

  if (items.length === 1) {
    const item = items[0];
    const ext = extFromPath(item.processedPath);
    await downloadBlob(item.downloadUrl, `${exportTitle}.${ext}`);
    return;
  }

  if (items.length > 1) {
    await Promise.all(
      items.map(async (item: any, idx: number) => {
        const ext = extFromPath(item.processedPath);
        await downloadBlob(item.downloadUrl, `${exportTitle}_${idx + 1}.${ext}`);
      })
    );
  }
}

/** "PDT (America/Los_Angeles)" — the abbreviation people recognise, plus the zone that names it. */
function browserTimezoneLabel(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const abbreviation = new Intl.DateTimeFormat(undefined, {
    timeZoneName: "short",
  })
    .formatToParts(new Date())
    .find((part) => part.type === "timeZoneName")?.value;
  return abbreviation ? `${abbreviation} (${zone})` : zone;
}

/** Default slot when nothing is set yet: the top of the next hour. */
function nextHour(): number {
  const date = new Date();
  date.setHours(date.getHours() + 1, 0, 0, 0);
  return date.getTime();
}

function isMultiImageZipExportResult(exportResult: any) {
  const items = exportResult?.items ?? [];
  return items.length > 1 && items.every((item: any) => item.type === "image");
}

function getExportDownloadLabel(exportResult: any, exportTitle: string) {
  if (!exportResult || !exportTitle) return null;

  if (exportResult.videoUrl) {
    return `${exportTitle}.mp4`;
  }

  if (exportResult.pdfDownloadUrl) {
    return `${exportTitle}.pdf`;
  }

  const items = exportResult.items ?? [];
  if (isMultiImageZipExportResult(exportResult)) {
    return `${exportTitle}.zip`;
  }

  if (items.length === 1) {
    return `${exportTitle}.${extFromPath(items[0].processedPath)}`;
  }

  if (items.length > 1) {
    return `${items.length} files`;
  }

  return null;
}

const DEFAULT_CAROUSEL_PREVIEW_DIMENSIONS = {
  width: 1080,
  height: 1350,
} as const;

const MOBILE_PREVIEW_MAX_HEIGHT_DVH = 50;
const MOBILE_PREVIEW_MAX_HEIGHT = `${MOBILE_PREVIEW_MAX_HEIGHT_DVH}dvh`;
// How long opening a draft waits for slide URLs before showing the editor
// with spinners. Healthy loads finish well inside this.
const MEDIA_FIRST_PASS_WAIT_MS = 2500;
/** How recently an account must have been (re)connected to count as just connected. */
const RECENT_CONNECT_MS = 30 * 60 * 1000;
// Waits between retries of a failed connections load before showing Retry.
const CONNECTIONS_RETRY_DELAYS_MS = [1000, 3000, 8000];
// A stalled server never fails the request, so each attempt gives up after
// this long and counts as a failure (healthy loads take well under 1s).
const CONNECTIONS_ATTEMPT_TIMEOUT_MS = 15000;
const NO_CONNECTIONS: PlatformConnection[] = [];

function getPreviewAspectSizing(
  dimensions?: Partial<PostMediaItem["metadata"]["dimensions"]>,
  fallback = DEFAULT_CAROUSEL_PREVIEW_DIMENSIONS
) {
  const width =
    typeof dimensions?.width === "number" && dimensions.width > 0
      ? dimensions.width
      : fallback.width;
  const height =
    typeof dimensions?.height === "number" && dimensions.height > 0
      ? dimensions.height
      : fallback.height;

  return {
    aspectRatio: `${width} / ${height}`,
    widthToHeightRatio: width / height,
  };
}

function getMobilePreviewStyle(
  sizing: ReturnType<typeof getPreviewAspectSizing>
): React.CSSProperties {
  return {
    aspectRatio: sizing.aspectRatio,
    maxHeight: MOBILE_PREVIEW_MAX_HEIGHT,
    width: `min(100%, ${MOBILE_PREVIEW_MAX_HEIGHT_DVH * sizing.widthToHeightRatio}dvh)`,
  };
}

function normalizeDefaultLink(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  const withProtocol = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const url = new URL(withProtocol);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return undefined;
    }
    return url.toString();
  } catch {
    return undefined;
  }
}

function getFirstUrlFromGenerationSource(
  generationSource?: string | null
): string | undefined {
  if (!generationSource || generationSource === "direct-text-input") {
    return undefined;
  }

  // Combined sources join with " + " and may include pasted text; uploaded
  // PDFs point at this app's private document pages. Only public pages count.
  return generationSource
    .split(/\s+\+\s+/)
    .map((source) => normalizeDefaultLink(source))
    .find((link) => {
      if (!link) return false;
      const { hostname, origin } = new URL(link);
      return (
        hostname.includes(".") &&
        origin !== window.location.origin &&
        hostname !== "app.wavegen.ai"
      );
    });
}

/** Memoised so parent re-renders don't interrupt native video playback. */
const SimpleVideoPlayer = React.memo(function SimpleVideoPlayer({
  src,
}: {
  src: string;
}) {
  return (
    <video
      src={src}
      className="max-w-full max-h-full"
      controls
      playsInline
    />
  );
});

export function CreatePost() {
  const { currentUser, activeWorkspaceId } = useAuth();
  const { currentPlan, isFreePlan, maxConnections } = useStripe();
  const { toast } = useToast();
  const navigate = useNavigate();
  const dashboardSidebar = useDashboardSidebar();
  const isMobile = useIsMobile();
  const [isScheduled, setIsScheduled] = useState(false);
  const [selectedConnections, setSelectedConnections] = useState(
    new Set<string>()
  );
  // An empty list only means "no channels" once loaded; while loading or
  // after a failed load, the publish modal must not ask to connect. Tied to
  // the workspace it was loaded for, so switching workspaces never shows the
  // previous workspace's channels while the new ones load.
  const [loadedConnections, setLoadedConnections] = useState<{
    workspaceId: string | null;
    status: ConnectionsLoadStatus;
    list: PlatformConnection[];
  }>({ workspaceId: null, status: "loading", list: NO_CONNECTIONS });
  const [connectionsReloadNonce, setConnectionsReloadNonce] = useState(0);
  const isCurrentWorkspaceConnections =
    loadedConnections.workspaceId === activeWorkspaceId;
  const connections = isCurrentWorkspaceConnections
    ? loadedConnections.list
    : NO_CONNECTIONS;
  const connectionsStatus: ConnectionsLoadStatus = isCurrentWorkspaceConnections
    ? loadedConnections.status
    : "loading";
  const connectionsLoaded = connectionsStatus === "loaded";
  const [commonSettings, setCommonSettings] = useState({
    description: "",
    scheduledTime: "",
    title: "",
    firstComment: "",
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  // Slide images can't load because Firebase can't reach Google's servers.
  const [mediaUnreachable, setMediaUnreachable] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [hasCriticalMediaErrors] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Carries the Drafts page this editor overlays; URL rewrites must keep it.
  // Without it the editor remounts as a full page and loses what the
  // consumed parameter was about to open (a modal, the advanced editor).
  const routeLocation = useLocation();
  const routeStateRef = useRef(routeLocation.state);
  routeStateRef.current = routeLocation.state;
  const draftIdFromUrl = searchParams.get("draftId");

  // Keep the URL's draftId in sync with the loaded/saved draft so the browser
  // Back button (e.g. returning from /upgrade) and refreshes restore this
  // draft instead of a blank editor. Uses replaceState rather than
  // setSearchParams so it doesn't re-trigger the draft-loading effect (which
  // keys off React Router's searchParams) and reload mid-edit.
  useEffect(() => {
    if (!draftId || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("draftId") === draftId) return;
    params.set("draftId", draftId);
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}?${params.toString()}`
    );
  }, [draftId]);

  // Redirect legacy deep-links to the new /generate page
  const articleUrlParam = searchParams.get("articleUrl");
  useEffect(() => {
    if (articleUrlParam) {
      const params = new URLSearchParams();
      params.set("articleUrl", articleUrlParam);
      const intentParam = searchParams.get("intent");
      if (intentParam) params.set("intent", intentParam);
      navigate(`/generate?${params.toString()}`, { replace: true });
    }
  }, [articleUrlParam, searchParams, navigate]);

  // Prefill scheduledTime from URL param (e.g. from marketing best-time-to-post pages)
  const scheduledTimeParam = searchParams.get("scheduledTime");
  const scheduledTimeAppliedRef = useRef(false);
  useEffect(() => {
    if (scheduledTimeAppliedRef.current) return;
    if (!scheduledTimeParam || draftIdFromUrl) return;
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(scheduledTimeParam)) return;
    const target = new Date(scheduledTimeParam);
    if (Number.isNaN(target.getTime()) || target.getTime() < Date.now()) {
      scheduledTimeAppliedRef.current = true;
      toast({ title: "Choose a new posting time", description: "The time you selected has passed. You can choose another before scheduling your post." });
      return;
    }

    scheduledTimeAppliedRef.current = true;
    // The publish modal's Schedule panel prefills this time for every channel.
    setCommonSettings((prev) =>
      prev.scheduledTime ? prev : { ...prev, scheduledTime: scheduledTimeParam }
    );

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("scheduledTime");
    setSearchParams(newParams, { replace: true });
  }, [scheduledTimeParam, draftIdFromUrl, searchParams, setSearchParams]);

  // Cache for overlay image URLs (preloaded for all slides to avoid flash on navigation)
  const [overlayImageUrlCache, setOverlayImageUrlCache] = useState<
    Record<string, string>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [isCloningFromShare, setIsCloningFromShare] = useState(false);
  const cloneAttemptedRef = useRef<string | null>(null); // Track which shareId we've attempted to clone
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [platformSettings, setPlatformSettings] = useState<
    Record<string, PlatformSpecificSettings[keyof PlatformSpecificSettings]>
  >({});
  // Channels the loaded draft carries without settings: attached outside the
  // editor (Autopilot, onboarding), so never seeded from what they remember.
  const [unseededConnectionIds, setUnseededConnectionIds] = useState<string[]>([]);
  const pinterestDescriptionRequestIdsRef = useRef<Record<string, string>>({});
  const [
    pinterestDescriptionGeneratingIds,
    setPinterestDescriptionGeneratingIds,
  ] = useState<Set<string>>(() => new Set());
  const [isMediaEditorOpen, setIsMediaEditorOpen] = useState(false);
  const { navigateToUpgrade } = useUpgradePage();
  const [isWatermarkDialogOpen, setIsWatermarkDialogOpen] = useState(false);
  const [watermarkDialogAction, setWatermarkDialogAction] = useState<
    "publish" | "export"
  >("publish");
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  // First-publish nudge when YouTube will auto-convert an unpreviewed carousel
  const [youtubeNudgeOpen, setYoutubeNudgeOpen] = useState(false);
  // Publish modal: channels, their settings and previews, and timing
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const tickWhenConnectionsLoadRef = useRef(false);
  const [publishStartScheduling, setPublishStartScheduling] = useState(false);
  const defaultFirstCommentRef = useRef("");
  // Open the modal on a channel (e.g. the YouTube nudge's "See it first")
  const [publishFocusRequest, setPublishFocusRequest] = useState<{
    connectionId: string;
    nonce: number;
  } | null>(null);
  // Set by the modal's buttons; published from an effect once the schedule
  // state it implies has rendered, so saveDraft reads the new times.
  const [publishIntent, setPublishIntent] = useState<"now" | "schedule" | null>(
    null
  );
  const [originalPost, setOriginalPost] = useState<Post | null>(null);
  const [actionToPerform, setActionToPerform] = useState<string | null>(null);
  const [pendingIntent, setPendingIntent] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get("intent")
  );
  const [isExporting, setIsExporting] = useState(false);
  const [isVideoPost, setIsVideoPost] = useState(false);
  const [exportResults, setExportResults] = useState<any>(null);
  const [exportTitle, setExportTitle] = useState<string>("carousel");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportLoadingModalOpen, setIsExportLoadingModalOpen] =
    useState(false);
  const [brandKitWebsite, setBrandKitWebsite] = useState<string | undefined>();
  const [mediaUpdateCounter, setMediaUpdateCounter] = useState(0);

  // Share state
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareId, setShareId] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);

  // Smart scheduling state
  // Not settable. A picked time is turned into an instant using the browser's zone and
  // stored zone-free, so no other zone can be honoured here — a control offering one would
  // be lying. Still passed to /schedule/suggest, where it picks the zone the default
  // posting slots are read in (connections with their own posting schedule ignore it).
  const targetTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneLabel = useMemo(browserTimezoneLabel, []);
  const [connectionSchedules, setConnectionSchedules] = useState<
    Record<string, { scheduledFor: number; platform: PlatformType }>
  >({});
  const [isCancelingSchedule, setIsCancelingSchedule] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  // Opened from a link after the post already went out everywhere.
  const [alreadyPublishedNotice, setAlreadyPublishedNotice] =
    useState<AlreadyPublishedNotice | null>(null);

  // Get published connections from the original post (for mixed state editing)
  const publishedAtById = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(originalPost?.connections || {})
          .filter(([, conn]) => conn.status === "published")
          .map(([id, conn]) => [id, conn.publishedAt ?? 0])
      ) as Record<string, number>,
    [originalPost?.connections]
  );
  const publishedConnectionIds = useMemo(() => Object.keys(publishedAtById), [publishedAtById]);

  // Check if any connections are already published (prevents adding new connections)
  const hasPublishedConnections = useMemo(
    () =>
      Object.values(originalPost?.connections || {}).some(
        (c) => c.status === "published"
      ),
    [originalPost?.connections]
  );

  // Check if there are pending connections with scheduled tasks that are currently selected
  const hasPendingWithTasks = useMemo(
    () =>
      Object.entries(originalPost?.connections || {}).some(
        ([id, c]) =>
          c.status === "pending" && (c.taskName || c.deferred) && selectedConnections.has(id)
      ),
    [originalPost?.connections, selectedConnections]
  );

  const defaultPinterestLink = useMemo(
    () =>
      getFirstUrlFromGenerationSource(originalPost?.generationSource) ||
      brandKitWebsite,
    [brandKitWebsite, originalPost?.generationSource]
  );

  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const isPopulatingFromDraft = useRef(false);
  // The draft the editor last loaded from the URL (see initializeState).
  const loadedDraftIdRef = useRef<string | null>(null);
  const suppressUnloadPromptRef = useRef(false);
  console.log("[CreatePost] Plan detection:", {
    currentPlan,
    currentPlanId: currentPlan?.id,
    stripePriceId: currentPlan?.stripePriceId,
    isFreePlan,
  });

  const hasUploadingItems = useMemo(() => {
    return uploadItems.some(
      (item) => item.uploadProgress !== undefined && item.uploadProgress < 100
    );
  }, [uploadItems]);

  useEffect(() => {
    if (mediaUnreachable) {
      posthog.capture("editor_media_unreachable", { draft_id: draftId });
    }
    // Once per time the banner appears, not per draft id change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaUnreachable]);

  // Platforms that support text-only posts (no media required)
  const TEXT_ONLY_PLATFORMS: PlatformType[] = ["linkedin"];

  // Media is required unless ALL selected connections are text-only capable
  const mediaRequired = useMemo(() => {
    if (selectedConnections.size === 0) return true;
    return Array.from(selectedConnections).some((id) => {
      const conn = connections.find((c) => c.id === id);
      return !conn || !TEXT_ONLY_PLATFORMS.includes(conn.platform);
    });
  }, [selectedConnections, connections]);

  const adjustTextareaHeight = useCallback(() => {
    if (descriptionTextareaRef.current) {
      const textarea = descriptionTextareaRef.current;
      textarea.style.height = "auto"; // Allow shrinking
      const animationFrameId = requestAnimationFrame(() => {
        if (descriptionTextareaRef.current) {
          // Check ref again inside animation frame
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      });
      return () => cancelAnimationFrame(animationFrameId); // Cleanup for requestAnimationFrame
    }
    return () => {}; // Return a no-op cleanup if ref is null initially
  }, []);

  // Preload overlay image URLs for all slides to prevent flash when navigating carousel
  useEffect(() => {
    let isMounted = true;

    const preloadOverlayUrls = async () => {
      const newUrls: Record<string, string> = {};

      // Collect all image overlay filePaths from all upload items
      for (const item of uploadItems) {
        const overlays = item.mediaItem?.metadata?.overlays || [];
        for (const overlay of overlays) {
          if (
            overlay.type === "image" &&
            "filePath" in overlay &&
            overlay.filePath &&
            !overlayImageUrlCache[overlay.filePath] &&
            !newUrls[overlay.filePath]
          ) {
            try {
              const storageRef = ref(storage, overlay.filePath);
              const url = await getSignedUrl(storageRef.fullPath);
              newUrls[overlay.filePath] = url;
            } catch (error) {
              console.error("Error preloading overlay image URL:", error);
            }
          }
        }
      }

      if (isMounted && Object.keys(newUrls).length > 0) {
        setOverlayImageUrlCache((prev) => ({ ...prev, ...newUrls }));
      }
    };

    preloadOverlayUrls();

    return () => {
      isMounted = false;
    };
  }, [uploadItems, overlayImageUrlCache]);

  useEffect(() => {
    if (!isLoading && commonSettings.description) {
      const cleanup = adjustTextareaHeight();
      return cleanup; // Return the cleanup function from adjustTextareaHeight
    }
    // If the condition isn't met, we might still want to ensure a cleanup if one was previously set
    // but in this specific logic, adjustTextareaHeight isn't called, so no specific cleanup is needed here.
    // However, if the text is cleared, the height won't reset. User said this is fine.
  }, [isLoading, commonSettings.description, adjustTextareaHeight]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isRedirectingToStripe || suppressUnloadPromptRef.current) {
        return;
      }

      if (hasUploadingItems || hasUnsavedChanges) {
        e.preventDefault();
        const message = hasUploadingItems
          ? "You have media uploads in progress. Are you sure you want to leave? Your uploads will be lost."
          : "You have unsaved changes. Are you sure you want to leave?";
        e.returnValue = message;
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUploadingItems, hasUnsavedChanges, isRedirectingToStripe]);

  const isOverConnectionLimit = connections.length > maxConnections;

  const resetToNewPostState = useCallback(() => {
    setCommonSettings({ description: "", scheduledTime: "", title: "", firstComment: "" });
    setIsScheduled(false);
    setSelectedConnections(new Set<string>());
    setPlatformSettings({});
    setUnseededConnectionIds([]);
    setUploadItems([]);
    setDraftId(null);
    setOriginalPost(null);
    setHasUnsavedChanges(false);
    setIsLoading(false);
  }, []);

  const handleCommonSettingsChange = (field: string, value: string) => {
    setCommonSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasUnsavedChanges(true);
  };

  const handleSettingsChange = useCallback(
    (
      connectionId: string,
      newSettings: Partial<
        PlatformSpecificSettings[keyof PlatformSpecificSettings]
      >
    ) => {
      if (Object.prototype.hasOwnProperty.call(newSettings, "description")) {
        delete pinterestDescriptionRequestIdsRef.current[connectionId];
        setPinterestDescriptionGeneratingIds((prev) => {
          if (!prev.has(connectionId)) return prev;
          const next = new Set(prev);
          next.delete(connectionId);
          return next;
        });
      }
      setPlatformSettings((prev) => ({
        ...prev,
        [connectionId]: {
          ...prev[connectionId],
          ...newSettings,
        } as PlatformSpecificSettings[keyof PlatformSpecificSettings],
      }));
      setHasUnsavedChanges(true);
    },
    []
  );

  const generatePinterestDescription = useCallback(
    async (
      connectionId: string,
      title: string,
      postDescription: string
    ) => {
      if (!currentUser || !postDescription.trim()) return;

      const requestId = uuidv4();
      pinterestDescriptionRequestIdsRef.current[connectionId] = requestId;
      setPinterestDescriptionGeneratingIds((prev) => {
        const next = new Set(prev);
        next.add(connectionId);
        return next;
      });

      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/connections/pinterest/${connectionId}/generate-description`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ title, postDescription }),
          }
        );
        if (!response.ok) {
          throw new Error("Pinterest description generation failed");
        }

        const data = (await response.json()) as { description?: string };
        const description = data.description?.trim();
        if (
          !description ||
          pinterestDescriptionRequestIdsRef.current[connectionId] !== requestId
        ) {
          return;
        }

        setPlatformSettings((prev) => {
          const current = prev[connectionId] as PinterestSettings | undefined;
          if (!current || current.description?.trim()) return prev;

          return {
            ...prev,
            [connectionId]: {
              ...current,
              description,
              descriptionSource: "generated",
            },
          };
        });
        setHasUnsavedChanges(true);
      } catch (error) {
        console.error("Failed to generate Pinterest description:", error);
      } finally {
        if (
          pinterestDescriptionRequestIdsRef.current[connectionId] === requestId
        ) {
          delete pinterestDescriptionRequestIdsRef.current[connectionId];
          setPinterestDescriptionGeneratingIds((prev) => {
            if (!prev.has(connectionId)) return prev;
            const next = new Set(prev);
            next.delete(connectionId);
            return next;
          });
        }
      }
    },
    [currentUser]
  );

  const handleToggleConnection = useCallback(
    (connectionId: string, selected: boolean) => {
      const connection = connections.find((item) => item.id === connectionId);

      setSelectedConnections((prev) => {
        const next = new Set(prev);
        if (selected) next.add(connectionId);
        else next.delete(connectionId);
        return next;
      });

      // Unticking keeps the channel's settings, so ticking it back in the
      // publish modal restores what was there. Only ticked channels save.
      setPlatformSettings((prev) => {
        if (!selected || !connection || prev[connectionId]) return prev;
        return {
          ...prev,
          [connectionId]: initialConnectionSettings(
            connection.platform,
            defaultSettings[connection.platform],
            connection.publishDefaults
          ),
        };
      });

      if (!selected) {
        delete pinterestDescriptionRequestIdsRef.current[connectionId];
        setPinterestDescriptionGeneratingIds((prev) => {
          if (!prev.has(connectionId)) return prev;
          const next = new Set(prev);
          next.delete(connectionId);
          return next;
        });
      } else if (
        connection?.platform === "pinterest" &&
        commonSettings.description.length > 800
      ) {
        void generatePinterestDescription(
          connectionId,
          commonSettings.title,
          commonSettings.description
        );
      }

      setHasUnsavedChanges(true);
    },
    [
      commonSettings.description,
      commonSettings.title,
      connections,
      generatePinterestDescription,
    ]
  );

  useEffect(() => {
    const workspaceId = activeWorkspaceId;
    if (!workspaceId) return;
    const controller = new AbortController();
    // Keep this workspace's last list (a retry), never another workspace's.
    const setStatus = (
      status: ConnectionsLoadStatus,
      list?: PlatformConnection[]
    ) =>
      setLoadedConnections((prev) => ({
        workspaceId,
        status,
        list:
          list ?? (prev.workspaceId === workspaceId ? prev.list : NO_CONNECTIONS),
      }));
    const fetchConnections = async () => {
      setStatus("loading");
      for (let attempt = 0; ; attempt++) {
        // Abort this attempt on unmount/workspace change or when it times out;
        // the race also covers the token step, which fetch's signal can't.
        const attemptController = new AbortController();
        const abortAttempt = () => attemptController.abort();
        controller.signal.addEventListener("abort", abortAttempt);
        let attemptTimeout: ReturnType<typeof setTimeout> | undefined;
        try {
          const fetchedConnections = await Promise.race([
            getAllValidConnectionsForUser(workspaceId, attemptController.signal),
            new Promise<never>((_, reject) => {
              attemptTimeout = setTimeout(() => {
                abortAttempt();
                reject(new Error("Loading connected accounts timed out."));
              }, CONNECTIONS_ATTEMPT_TIMEOUT_MS);
            }),
          ]);
          if (controller.signal.aborted) return;
          setStatus("loaded", fetchedConnections);
          return;
        } catch (err) {
          if (controller.signal.aborted) return;
          console.error("Error fetching connections:", err);
          if (attempt >= CONNECTIONS_RETRY_DELAYS_MS.length) {
            posthog.capture("connections_load_failed", {
              surface: "create_post",
              attempts: attempt + 1,
              error: err instanceof Error ? err.message : String(err),
            });
            setStatus("error");
            return;
          }
        } finally {
          clearTimeout(attemptTimeout);
          controller.signal.removeEventListener("abort", abortAttempt);
        }
        await new Promise((resolve) =>
          setTimeout(resolve, CONNECTIONS_RETRY_DELAYS_MS[attempt])
        );
        if (controller.signal.aborted) return;
      }
    };
    fetchConnections();
    return () => controller.abort();
  }, [activeWorkspaceId, connectionsReloadNonce]);

  // Seed those channels the way the publish modal seeds a newly ticked one,
  // so first comment, format and the rest follow the channel's last post.
  useEffect(() => {
    if (!connectionsLoaded || unseededConnectionIds.length === 0) return;
    setPlatformSettings((prev) => {
      const next = { ...prev };
      for (const id of unseededConnectionIds) {
        const connection = connections.find((item) => item.id === id);
        if (!connection || !prev[id]) continue;
        next[id] = initialConnectionSettings(
          connection.platform,
          prev[id],
          connection.publishDefaults
        );
      }
      return next;
    });
    setUnseededConnectionIds([]);
  }, [connectionsLoaded, connections, unseededConnectionIds]);

  useEffect(() => {
    let cancelled = false;
    const brandKitId = originalPost?.brandKitId;

    if (!brandKitId || !activeWorkspaceId) {
      setBrandKitWebsite(undefined);
      return;
    }

    const fetchBrandKitWebsite = async () => {
      try {
        const brandKitSnap = await getDoc(doc(db, "brand_kits", brandKitId));
        if (cancelled) return;

        if (!brandKitSnap.exists()) {
          setBrandKitWebsite(undefined);
          return;
        }

        const brandKit = {
          id: brandKitSnap.id,
          ...brandKitSnap.data(),
        } as BrandKit;
        setBrandKitWebsite(
          brandKit.userId === activeWorkspaceId
            ? normalizeDefaultLink(brandKit.website)
            : undefined
        );
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching brand kit website:", error);
          setBrandKitWebsite(undefined);
        }
      }
    };

    fetchBrandKitWebsite();

    return () => {
      cancelled = true;
    };
  }, [activeWorkspaceId, originalPost?.brandKitId]);

  const populateFormFromDraft = useCallback(async (draft: Post) => {
    isPopulatingFromDraft.current = true;

    setCommonSettings({
      description: draft.description || "",
      scheduledTime: formatDate(draft.scheduledFor, "datetime-local"),
      title: draft.title || "",
      firstComment: draft.firstComment || "",
    });

    setIsScheduled(Boolean(draft.scheduledFor && draft.scheduledFor > 0));

    // Only select pending connections for editing - published ones are read-only
    const selectedConnectionIds = new Set(
      Object.entries(draft.connections)
        .filter(([, conn]) => conn.status !== "published")
        .map(([id]) => id)
    );
    setSelectedConnections(selectedConnectionIds);

    // Populate per-connection schedules if they exist
    const perConnectionSchedules: Record<
      string,
      { scheduledFor: number; platform: PlatformType }
    > = {};
    for (const [id, connection] of Object.entries(draft.connections)) {
      if (connection.scheduledFor && connection.scheduledFor > 0) {
        perConnectionSchedules[id] = {
          scheduledFor: connection.scheduledFor,
          platform: connection.platform,
        };
      }
    }
    setConnectionSchedules(perConnectionSchedules);

    // Reset flag after a tick to allow useEffect to see it
    setTimeout(() => {
      isPopulatingFromDraft.current = false;
    }, 0);

    const settings = Object.entries(draft.connections).reduce(
      (acc, [id, connection]) => {
        const loaded = (connection.settings ||
          defaultSettings[connection.platform]) as PlatformSpecificSettings[keyof PlatformSpecificSettings] &
          FirstCommentOverrides;
        // Posts saved before first comments were per channel commented on
        // every supported channel whenever the post had a comment. Keep that.
        acc[id] =
          FIRST_COMMENT_PLATFORMS.has(connection.platform) &&
          loaded.firstCommentEnabled === undefined
            ? { ...loaded, firstCommentEnabled: !!draft.firstComment }
            : loaded;
        return acc;
      },
      {} as Record<
        string,
        PlatformSpecificSettings[keyof PlatformSpecificSettings]
      >
    );

    setPlatformSettings(settings);
    setUnseededConnectionIds(
      Object.entries(draft.connections)
        .filter(([, connection]) => !connection.settings && connection.status !== "published")
        .map(([id]) => id)
    );
    setMediaUnreachable(false);
    if (draft.mediaItems?.length) {
      // Sign every slide URL in parallel, but only hold the loading screen
      // for a moment. When Firebase Auth can't reach Google, each attempt
      // hangs 30s before failing; the editor opens with per-slide spinners
      // instead and the URLs land whenever they arrive.
      const signedUrls = new Map<string, string>();
      const entries = draft.mediaItems.map((item) => {
        const uploadItem = {
          id: uuidv4(),
          type: item.type,
          preview: item.path,
          mediaItem: item,
          uploadProgress: 100,
        } as UploadItem;
        const firstAttempt =
          item.path &&
          (!item.path.startsWith("http") || isFirebaseStorageUrl(item.path))
            ? getSignedUrl(item.path).then((url) => {
                signedUrls.set(uploadItem.id, url);
                return url;
              })
            : null;
        return { uploadItem, firstAttempt };
      });

      await Promise.race([
        Promise.allSettled(entries.map((e) => e.firstAttempt)),
        new Promise((r) => setTimeout(r, MEDIA_FIRST_PASS_WAIT_MS)),
      ]);

      // Signed ones show their image; the rest show a spinner (0 = loading).
      setUploadItems(
        entries.map(({ uploadItem, firstAttempt }) => {
          if (!firstAttempt) return uploadItem;
          const url = signedUrls.get(uploadItem.id);
          return url
            ? { ...uploadItem, preview: url }
            : { ...uploadItem, uploadProgress: 0 };
        })
      );

      const setItem = (id: string, patch: Partial<UploadItem>) =>
        setUploadItems((current) =>
          current.map((it) => (it.id === id ? { ...it, ...patch } : it))
        );

      for (const { uploadItem, firstAttempt } of entries) {
        if (!firstAttempt || signedUrls.has(uploadItem.id)) continue;
        const path = uploadItem.mediaItem!.path;
        (async () => {
          // The first attempt may still be in flight; then up to 3 retries.
          for (let attempt = 0; attempt <= 3; attempt++) {
            try {
              if (attempt > 0) {
                await new Promise((r) => setTimeout(r, 1000 * attempt));
              }
              const url = await (attempt === 0 ? firstAttempt : getSignedUrl(path));
              setMediaUnreachable(false);
              setItem(uploadItem.id, { preview: url, uploadProgress: 100 });
              return;
            } catch (error) {
              if (isFirebaseNetworkError(error)) setMediaUnreachable(true);
            }
          }
          console.warn(`[CreatePost] Failed to load preview after retries: ${path}`);
          setItem(uploadItem.id, {
            error: "Failed to load media",
            uploadProgress: 100,
          });
        })();
      }
    } else {
      setUploadItems([]);
    }
  }, []);

  useEffect(() => {
    const initializeState = async () => {
      // Get fresh params directly, as searchParams from state might not be updated if effect is re-triggered rapidly
      const currentQueryProps = new URLSearchParams(window.location.search);
      const draftIdFromCurrentQuery = currentQueryProps.get("draftId");
      const cloneShareIdFromQuery = currentQueryProps.get("cloneShareId");
      const actionFromQuery = currentQueryProps.get("action");

      // Tidying other params (intent, connect status) must not reload the
      // draft that's already open — that would wipe unsaved selections, like
      // the channel just connected from the publish modal.
      if (
        draftIdFromCurrentQuery &&
        draftIdFromCurrentQuery === loadedDraftIdRef.current &&
        !cloneShareIdFromQuery &&
        !actionFromQuery
      ) {
        return;
      }
      if (!draftIdFromCurrentQuery) loadedDraftIdRef.current = null;
      setIsLoading(true);

      // Add a guard clause for currentUser
      if (!currentUser) {
        setIsLoading(false);
        console.log(
          "[CreatePost] Current user not found, aborting initialization."
        );
        return;
      }

      // Handle cloning from share page
      if (cloneShareIdFromQuery) {
        // Guard against double-cloning (React strict mode, etc.)
        if (cloneAttemptedRef.current === cloneShareIdFromQuery) {
          console.log(
            "[CreatePost] Clone already attempted for:",
            cloneShareIdFromQuery
          );
          setIsLoading(false);
          return;
        }
        cloneAttemptedRef.current = cloneShareIdFromQuery;

        console.log("[CreatePost] Cloning from share:", cloneShareIdFromQuery);
        setIsCloningFromShare(true);

        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE}/api/share/${cloneShareIdFromQuery}/clone`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${await currentUser.getIdToken()}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || "Failed to clone post");
          }

          const { postId } = await response.json();
          console.log("[CreatePost] Post cloned successfully:", postId);

          // Redirect to /created — cloned post appears at top as newest draft
          navigate(`/created`, { replace: true });

          toast({
            title: "Post saved to your account!",
            description: "You can now edit and customize this post.",
          });
        } catch (error: any) {
          console.error("[CreatePost] Error cloning from share:", error);
          toast({
            variant: "destructive",
            title: "Clone Failed",
            description:
              error.message ||
              "Failed to save the shared post. Please try again.",
          });
          // Clean up URL on error
          setSearchParams(new URLSearchParams(), { replace: true });
          resetToNewPostState();
        } finally {
          setIsCloningFromShare(false);
          setIsLoading(false);
        }
        return;
      }

      if (draftIdFromCurrentQuery && activeWorkspaceId) {
        try {
          const draft = await getPostForUser(
            draftIdFromCurrentQuery,
            activeWorkspaceId
          );
          if (draft) {
            setDraftId(draftIdFromCurrentQuery);
            setOriginalPost(draft);
            await populateFormFromDraft(draft);
            loadedDraftIdRef.current = draftIdFromCurrentQuery;
            setHasUnsavedChanges(false);
          } else {
            toast({
              variant: "destructive",
              title: "Draft Not Found",
              description: `Could not load draft with ID: ${draftIdFromCurrentQuery}. You can create a new post.`,
            });
            resetToNewPostState();
            setDraftId(null);
          }
        } catch (error) {
          console.error("Error initializing draft from URL:", error);
          toast({
            title: "Couldn't load draft",
            description:
              "There was an issue loading your draft. Please try refreshing the page.",
          });
          // Don't wipe the editor — keep whatever state was loaded.
          // Only clear draftId if nothing was loaded at all.
          if (!draftIdFromCurrentQuery) {
            setDraftId(null);
          }
        }
      }

      // Clean up action param from URL after consuming it
      if (actionFromQuery) {
        const newSearchParams = new URLSearchParams(currentQueryProps);
        setActionToPerform(actionFromQuery);
        newSearchParams.delete("action");
        setSearchParams(newSearchParams, { replace: true, state: routeStateRef.current });
      }
      setIsLoading(false);
    };

    initializeState();
  }, [
    populateFormFromDraft,
    resetToNewPostState,
    toast,
    searchParams,
    setSearchParams,
    currentUser,
    activeWorkspaceId,
  ]);

  // Sync share state when originalPost changes
  useEffect(() => {
    if (originalPost) {
      setShareEnabled(originalPost.shareEnabled || false);
      setShareId(originalPost.shareId || null);
    }
  }, [originalPost?.shareEnabled, originalPost?.shareId]);

  // Back from connecting an account in the publish modal: the server sends
  // the callback here with its outcome (the intent param reopens the modal).
  const connectOutcomeHandledRef = useRef(false);
  const [connectedPlatformToast, setConnectedPlatformToast] = useState<string | null>(null);
  useEffect(() => {
    if (connectOutcomeHandledRef.current) return;
    const status = searchParams.get("status");
    if (status !== "success" && status !== "error") return;
    connectOutcomeHandledRef.current = true;
    // Settings' fallback hint is spent; don't let a later visit redirect.
    readReturnToPostTask(true);
    if (status === "error") {
      toast({
        variant: "destructive",
        title: "Couldn't connect that account",
        description: getConnectionErrorMessage(searchParams.get("error") || ""),
      });
    } else {
      setConnectedPlatformToast(searchParams.get("platform") ?? "");
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("status");
        next.delete("platform");
        next.delete("error");
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams, toast]);

  // A browser restoring an old tab replays the callback URL weeks later;
  // only announce a connection that was just made.
  useEffect(() => {
    if (connectedPlatformToast === null || !connectionsLoaded) return;
    const platform = connectedPlatformToast;
    setConnectedPlatformToast(null);
    const justConnected = connections.some(
      (connection) =>
        (!platform || connection.platform === platform) &&
        Math.max(connection.createdAt || 0, connection.updatedAt || 0) > Date.now() - RECENT_CONNECT_MS
    );
    if (!justConnected) return;
    const name = platform ? getPlatformConfig(platform as PlatformType)?.name : undefined;
    toast({ title: name ? `${name} connected` : "Account connected" });
  }, [connectedPlatformToast, connectionsLoaded, connections, toast]);

  // Handle intent from website carousel continuity flow
  useEffect(() => {
    if (!pendingIntent || !draftId || !originalPost || isLoading) return;

    if (["schedule", "publish-now", "publish"].includes(pendingIntent)) {
      // Wait for channels; if they fail to load, open the modal anyway so it
      // can say so and offer Try again. With no channels yet, the modal itself
      // offers the connect buttons and brings people back here afterwards.
      if (connectionsStatus === "loading") return;
    }

    // Clear the intent (and any connect outcome) from URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("intent");
    newParams.delete("status");
    newParams.delete("platform");
    newParams.delete("error");
    newParams.delete("from");
    setSearchParams(newParams, { replace: true, state: routeStateRef.current });

    // Handle intent-specific UX
    switch (pendingIntent) {
      case "remove-watermark":
        // Navigate to upgrade page for watermark removal
        if (isFreePlan) {
          navigateToUpgrade({ reason: "watermark" });
        }
        break;
      case "schedule":
      case "publish-now":
      case "publish": {
        // An old link or a restored tab can land here after the post went
        // out everywhere; the modal would only offer channels nobody can tick.
        const channels = connections.filter((connection) => !!getPlatformConfig(connection.platform));
        if (
          channels.length > 0 &&
          channels.every((connection) => publishedConnectionIds.includes(connection.id))
        ) {
          setAlreadyPublishedNotice({
            channelNames: channels.map((connection) => connection.profileName),
            publishedAt: Math.max(...channels.map((connection) => publishedAtById[connection.id] ?? 0)),
          });
          posthog.capture("publish_modal_skipped", {
            postId: originalPost?.id ?? draftId ?? undefined,
            intent: pendingIntent,
            reason: "already_published",
          });
          break;
        }
        if (pendingIntent === "schedule") {
          openPublishModal({ scheduling: true, tickRecentlyConnected: true, source: "intent_schedule" });
        } else if (pendingIntent === "publish-now") {
          openPublishModal({ tickRecentlyConnected: true, source: "intent_publish_now" });
        } else {
          // Where the Publish button was: the Drafts card or the post viewer.
          openPublishModal({ source: searchParams.get("from") || "drafts_card" });
        }
        break;
      }
      case "edit-advanced":
        // Navigate to the advanced editor
        if (uploadItems.length > 0) {
          setIsMediaEditorOpen(true);
        }
        break;
      case "brand-kit":
        // Just show a message - the actual brand kit flow happens elsewhere
        toast({
          title: "Apply your branding",
          description: "Select a brand kit to apply your colors and logo.",
        });
        break;
    }

    // Clear the pending intent
    setPendingIntent(null);
  }, [pendingIntent, draftId, originalPost, isLoading, isFreePlan, uploadItems.length, connectionsStatus, connectionsLoaded, connections.length]);

  // Handle enabling share
  const handleEnableShare = async () => {
    if (!currentUser || !draftId) {
      toast({
        variant: "destructive",
        title: "Cannot share",
        description: "Please save the post first before sharing.",
      });
      return;
    }

    setShareLoading(true);
    try {
      await prepareMediaForProcessing();

      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/posts/${draftId}/share`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ enabled: true }),
        }
      );

      if (!response.ok) throw new Error("Failed to update share settings");

      const data = await response.json();
      setShareEnabled(data.shareEnabled);
      setShareId(data.shareId);
      posthog.capture("share_link_enabled", { postId: draftId });

      if (data.shareEnabled && data.shareUrl) {
        try {
          await navigator.clipboard.writeText(data.shareUrl);
          toast({
            title: "Sharing enabled!",
            description: "Share link copied to clipboard.",
          });
        } catch (clipboardErr) {
          console.error("Clipboard write failed:", clipboardErr);
          toast({
            title: "Sharing enabled!",
            description: "Link is ready — click the copy button to copy it.",
          });
        }
      }
    } catch (err) {
      console.error("Error enabling share:", err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to enable sharing.",
      });
    } finally {
      setShareLoading(false);
    }
  };

  // Handle disabling share
  const handleDisableShare = async () => {
    if (!currentUser || !draftId) return;

    setShareLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/posts/${draftId}/share`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ enabled: false }),
        }
      );

      if (!response.ok) throw new Error("Failed to update share settings");

      const data = await response.json();
      setShareEnabled(data.shareEnabled);
      setShareId(data.shareId);
      posthog.capture("share_link_disabled", { postId: draftId });

      toast({
        title: "Sharing disabled",
        description: "This post is no longer publicly accessible.",
      });
    } catch (err) {
      console.error("Error disabling share:", err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to disable sharing.",
      });
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareId) return;
    const url = `${window.location.origin}/share/${shareId}`;

    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied!" });
    } catch (err) {
      console.error("Clipboard write failed:", err);
      toast({
        variant: "destructive",
        title: "Copy failed",
        description: "Unable to copy link.",
      });
    }
  };

  const saveDraft = useCallback(
    async (options?: {
      mediaItemsOverride?: PostMediaItem[];
      draftIdOverride?: string;
    }) => {
      if (!activeWorkspaceId || !currentUser) return null;

      // Ensure a draft ID exists, creating one if necessary.
      let currentDraftId = options?.draftIdOverride || draftId;
      if (!currentDraftId) {
        console.log("Creating new draft for user as no draftId was present.");
        const newDraftId = await createNewDraftForWorkspace(
          activeWorkspaceId,
          currentUser.uid
        );
        if (newDraftId) {
          currentDraftId = newDraftId;
        } else {
          console.error("Failed to create a new draft ID.");
          return null;
        }
      }

      // **CRITICAL FIX**: Always create the document reference with the converter.
      // This ensures that both reads (`getDoc`) and writes (`setDoc`) use our logic.
      const draftRef = doc(db, "posts", currentDraftId).withConverter(
        postConverter
      );

      const itemsToSave =
        options?.mediaItemsOverride ||
        compact(uploadItems.map((item) => item.mediaItem));

      // Build connections object first so we can derive status from it
      const connectionsToSave: Record<string, ConnectionPost> = {
        // First, preserve published connections from originalPost (they shouldn't be modified)
        ...Object.entries(originalPost?.connections || {}).reduce(
          (acc, [id, conn]) => {
            if (conn.status === "published") {
              acc[id] = conn;
            }
            return acc;
          },
          {} as Record<string, ConnectionPost>
        ),
        // Then add/update pending connections from selectedConnections
        ...Array.from(selectedConnections).reduce(
          (acc, connectionId) => {
            const connection = connections.find((c) => c.id === connectionId);
            if (!connectionsLoaded) {
              // Channels not loaded (slow or failed): keep what's saved, or a
              // save would drop scheduled channels and their posts never go
              // out. Nothing per-channel can change until they load.
              const savedConnection = originalPost?.connections?.[connectionId];
              if (savedConnection) acc[connectionId] = savedConnection;
            } else if (connection) {
              const originalConnection =
                originalPost?.connections[connectionId];
              // Use per-connection schedule if set, otherwise fall back to original
              const perConnSchedule = connectionSchedules[connectionId]?.scheduledFor;
              const scheduledFor = perConnSchedule
                ?? originalConnection?.scheduledFor;
              acc[connectionId] = {
                connectionId,
                platform: connection.platform,
                status: "pending",
                settings: platformSettings[connectionId],
                taskName: originalConnection?.taskName,
                scheduledFor,
              };
            }
            return acc;
          },
          {} as Record<string, ConnectionPost>
        ),
      };

      const draftData: Partial<Post> = {
        userId: activeWorkspaceId,
        workspaceId: activeWorkspaceId,
        status: derivePostStatus(connectionsToSave),
        mediaItems: itemsToSave,
        description: commonSettings.description,
        title: commonSettings.title,
        // Channels inherit this text; when one comments and the post has no
        // text of its own, the article-link default is what posts.
        firstComment:
          commonSettings.firstComment ||
          (Array.from(selectedConnections).some(
            (id) =>
              (platformSettings[id] as FirstCommentOverrides | undefined)
                ?.firstCommentEnabled
          )
            ? defaultFirstCommentRef.current
            : "") ||
          undefined,
        brandKitId: originalPost?.brandKitId || undefined,
        scheduledFor: isScheduled
          ? new Date(commonSettings.scheduledTime).getTime()
          : 0,
        connections: connectionsToSave,
        updatedAt: Date.now(),
      };

      try {
        // Video settings save independently for responsive previews. Make
        // sure this whole-document save reads after the latest setting write.
        await videoSettingsSaveQueueRef.current;
        const currentDoc = await getDoc(draftRef); // Read using the converter
        const currentData = (
          currentDoc.exists() ? currentDoc.data() : {}
        ) as Partial<Post>;

        const newData: Post = {
          ...currentData,
          ...draftData,
          // Channels not loaded: leave channels (and the status they drive)
          // exactly as stored right now, not as they were when the page
          // loaded, so a status the server changed since is never undone.
          ...(!connectionsLoaded && currentData.connections
            ? { connections: currentData.connections, status: currentData.status }
            : {}),
          id: currentDraftId, // Ensure ID is always set
          createdAt: currentData?.createdAt || draftData.updatedAt, // Preserve original creation time
        } as Post;

        console.log(
          `[SaveDraft] Saving to ID: ${currentDraftId} with data:`,
          newData
        );

        // Cancel tasks for connections that were removed (had taskName but no longer selected)
        // Do this BEFORE saving so the cancel endpoint can find the connection and cancel its task
        if (originalPost?.connections) {
          const removedConnectionsWithTasks = Object.entries(originalPost.connections)
            .filter(([id, conn]) =>
              conn.status === "pending" &&
              (conn.taskName || conn.deferred) &&
              !selectedConnections.has(id)
            );

          if (removedConnectionsWithTasks.length > 0) {
            const idToken = await currentUser.getIdToken();
            await Promise.all(
              removedConnectionsWithTasks.map(async ([connectionId]) => {
                try {
                  await fetch(
                    `${process.env.REACT_APP_API_BASE}/schedule/cancel`,
                    {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${idToken}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        postId: currentDraftId,
                        connectionId,
                      }),
                    }
                  );
                } catch (error) {
                  console.error(`Error canceling task for connection ${connectionId}:`, error);
                }
              })
            );
          }
        }

        // Write using the same converter-equipped ref
        await setDoc(draftRef, newData);

        console.log("Draft saved with ID:", currentDraftId);
        return currentDraftId;
      } catch (error) {
        console.error("Error saving draft to Firestore:", error);
        return null;
      }
    },
    [
      commonSettings.description,
      commonSettings.title,
      commonSettings.scheduledTime,
      commonSettings.firstComment,
      connectionSchedules,
      connections,
      connectionsLoaded,
      currentUser,
      activeWorkspaceId,
      draftId, // Keep draftId here as a dependency to get the latest value from state for the initial check
      isScheduled,
      platformSettings,
      selectedConnections,
      uploadItems, // Keep uploadItems for the default case when no override is provided
      originalPost,
    ]
  );

  const handleEditClick = useCallback(async () => {
    let currentDraftId = draftId;
    if (!currentDraftId && activeWorkspaceId && currentUser) {
      if (
        hasUnsavedChanges ||
        commonSettings.description ||
        uploadItems.length > 0
      ) {
        console.log(
          "Attempting to save draft before opening MediaEditor as draftId is missing."
        );
        currentDraftId = await saveDraft();
      } else {
        console.log(
          "Creating a new draft for MediaEditor as draftId is missing and no unsaved changes."
        );
        currentDraftId = await createNewDraftForWorkspace(
          activeWorkspaceId,
          currentUser.uid
        );
        if (currentDraftId) setDraftId(currentDraftId);
      }

      if (!currentDraftId) {
        toast({
          title: "Cannot Open Editor",
          description:
            "Failed to prepare draft for editing. Please save your post and try again.",
          variant: "destructive",
        });
        return;
      }
    }

    setIsMediaEditorOpen(true);
  }, [
    draftId,
    activeWorkspaceId,
    hasUnsavedChanges,
    commonSettings.description,
    uploadItems.length,
    saveDraft,
    toast,
    currentUser,
  ]);

  useEffect(() => {
    if (actionToPerform === "edit" && !isLoading) {
      handleEditClick();
      setActionToPerform(null); // Reset after performing
    }
  }, [actionToPerform, isLoading, handleEditClick]);

  const getMediaMetadata = async (
    file: File
  ): Promise<PostMediaItem["metadata"]> => {
    return new Promise((resolve) => {
      if (file.type.startsWith("video")) {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
          resolve({
            duration: video.duration,
            dimensions: {
              width: video.videoWidth,
              height: video.videoHeight,
            },
            size: file.size,
          });
        };
        video.src = URL.createObjectURL(file);
      } else {
        const img: HTMLImageElement = document.createElement("img");
        img.onload = () => {
          resolve({
            dimensions: {
              width: img.width,
              height: img.height,
            },
            size: file.size,
          });
        };
        img.src = URL.createObjectURL(file);
      }
    });
  };

  const generateVideoThumbnail = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      video.autoplay = true;
      video.muted = true;
      video.src = URL.createObjectURL(file);

      video.onloadeddata = () => {
        video.currentTime = 0;
      };

      video.onseeked = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        context?.drawImage(video, 0, 0, canvas.width, canvas.height);

        const thumbnailUrl = canvas.toDataURL("image/png");

        URL.revokeObjectURL(video.src);
        resolve(thumbnailUrl);
      };
    });
  };

  const handleSave = useCallback(async (options?: { silent?: boolean }): Promise<string | null> => {
    if (isSaving) return draftId;
    const silent = options?.silent ?? false;

    let resultDraftId: string | null = draftId;
    try {
      setIsSaving(true);
      const savedDraftId = await saveDraft();
      if (savedDraftId) {
        resultDraftId = savedDraftId;
        if (!draftId) {
          setDraftId(savedDraftId);
        }
        setHasUnsavedChanges(false);
        if (!silent) {
          const isPublishedPost = hasPublishedConnections;
          toast({
            title: isPublishedPost ? "Post saved" : "Draft saved successfully",
            description: isPublishedPost
              ? "Your changes have been saved."
              : "Your draft has been saved.",
          });
        }
      } else if (!silent) {
        toast({
          variant: "destructive",
          title: "Error Saving",
          description: "Could not save your changes. Please try again.",
        });
      }
    } catch (error) {
      console.error("Save failed:", error);
      if (!silent) {
        toast({
          variant: "destructive",
          title: "Error Saving",
          description: "An unexpected error occurred. Please try again.",
        });
      }
    } finally {
      setTimeout(() => {
        setIsSaving(false);
      }, 1000);
    }
    return resultDraftId;
  }, [isSaving, saveDraft, toast, draftId, hasPublishedConnections]);

  // Persist + URL-stamp the draft before any navigation that leaves the editor
  // (e.g. an upgrade CTA). Without this, clicking upgrade before autosave fires
  // would lose the draft and the browser Back button would return to a blank
  // editor. Registered via refs so the flush always uses the latest state
  // without re-registering on every render.
  const flushDraftIdRef = useRef(draftId);
  const flushUnsavedRef = useRef(hasUnsavedChanges);
  const flushHandleSaveRef = useRef(handleSave);
  flushDraftIdRef.current = draftId;
  flushUnsavedRef.current = hasUnsavedChanges;
  flushHandleSaveRef.current = handleSave;
  useEffect(() => {
    setEditorFlush(async () => {
      let id = flushDraftIdRef.current;
      if (flushUnsavedRef.current || !id) {
        id = await flushHandleSaveRef.current({ silent: true });
      }
      if (id && typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        if (params.get("draftId") !== id) {
          params.set("draftId", id);
          window.history.replaceState(
            window.history.state,
            "",
            `${window.location.pathname}?${params.toString()}`
          );
        }
      }
    });
    return () => setEditorFlush(null);
  }, []);

  // Auto-save: debounce 2s after changes
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!hasUnsavedChanges || hasUploadingItems) return;

    autoSaveTimerRef.current = setTimeout(() => {
      handleSave({ silent: true });
    }, 2000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, hasUploadingItems, handleSave]);

  const updateUploadItem = (id: string, updates: Partial<UploadItem>) => {
    setUploadItems((prevItems) => {
      const itemIndex = prevItems.findIndex((item) => item.id === id);
      if (itemIndex === -1) {
        console.warn(`updateUploadItem: Item with id ${id} not found.`);
        return prevItems;
      }
      const newItems = [...prevItems];
      newItems[itemIndex] = { ...newItems[itemIndex], ...updates };
      return newItems;
    });
    setHasUnsavedChanges(true);
  };

  const handleReplaceMediaItem = async (idToReplace: string, newFile: File) => {
    if (!activeWorkspaceId) {
      updateUploadItem(idToReplace, {
        error: "User not found. Please try again.",
        uploadProgress: undefined,
      });
      return;
    }

    let processedFile = newFile;
    try {
      const isValidType = ALLOWED_TYPE_PATTERNS.some((pattern) =>
        pattern.test(processedFile.type)
      );
      if (!isValidType) {
        updateUploadItem(idToReplace, {
          error: "Invalid file type",
          uploadProgress: undefined,
        });
        return;
      }

      if (processedFile.size > MAX_FILE_SIZE) {
        updateUploadItem(idToReplace, {
          error: "File too large",
          uploadProgress: undefined,
        });
        return;
      }

      processedFile = await convertHeicToPng(processedFile);
    } catch (conversionError) {
      console.error("Error during file pre-processing:", conversionError);
      updateUploadItem(idToReplace, {
        error: "Failed to process file.",
        uploadProgress: undefined,
      });
      return;
    }

    const newFileType = processedFile.type.startsWith("video")
      ? "video"
      : "image";

    const newBaseFileMetadata = await getMediaMetadata(processedFile);

    let adjustedMetadata: PostMediaItem["metadata"] = {
      ...newBaseFileMetadata,
      overlays: [],
      audioSettings: undefined,
      thumbnailPath: undefined,
      coverTimestamp: undefined,
      isCustomCover: false,
      coverViewMode: undefined,
    };

    const currentItemState = uploadItems.find(
      (item) => item.id === idToReplace
    );
    const oldMediaItem = currentItemState?.mediaItem;

    if (oldMediaItem?.metadata?.overlays?.length) {
      const newDimensions = newBaseFileMetadata.dimensions;
      const newDuration = newBaseFileMetadata.duration;

      adjustedMetadata.overlays = oldMediaItem.metadata.overlays.map(
        (overlay) => {
          const newOverlay = { ...overlay };
          newOverlay.position = { ...newOverlay.position };
          if (newOverlay.timing) {
            newOverlay.timing = { ...newOverlay.timing };
          }

          const overlayWidth = newOverlay.width || 100;
          newOverlay.position.x = Math.max(
            0,
            Math.min(newOverlay.position.x, newDimensions.width - overlayWidth)
          );
          const overlayHeight = isTextOverlay(newOverlay)
            ? newOverlay.style.fontSize || 20
            : 20; // Fallback height for image overlay
          newOverlay.position.y = Math.max(
            0,
            Math.min(
              newOverlay.position.y,
              newDimensions.height - overlayHeight
            )
          );
          if (newOverlay.width && newOverlay.width > newDimensions.width) {
            newOverlay.width = newDimensions.width;
          }

          if (newFileType === "video" && newDuration !== undefined) {
            if (!newOverlay.timing) {
              newOverlay.timing = { startTime: 0, endTime: newDuration };
            } else {
              if (
                newOverlay.timing.startTime === undefined ||
                newOverlay.timing.endTime === undefined
              ) {
                newOverlay.timing.startTime = 0;
                newOverlay.timing.endTime = newDuration;
              } else {
                if (newOverlay.timing.startTime >= newDuration) {
                  newOverlay.timing.startTime = 0;
                  newOverlay.timing.endTime = newDuration;
                } else {
                  newOverlay.timing.endTime = Math.min(
                    newOverlay.timing.endTime,
                    newDuration
                  );
                  if (
                    newOverlay.timing.startTime >= newOverlay.timing.endTime
                  ) {
                    newOverlay.timing.startTime = 0;
                  }
                }
              }
            }
          } else {
            newOverlay.timing = undefined;
          }
          return newOverlay;
        }
      );
    }

    if (oldMediaItem?.metadata?.audioSettings) {
      const potentialAudioSettings = { ...oldMediaItem.metadata.audioSettings };
      const newDuration = newBaseFileMetadata.duration;

      if (
        newFileType === "video" &&
        newDuration !== undefined &&
        potentialAudioSettings.startTime !== undefined &&
        potentialAudioSettings.endTime !== undefined
      ) {
        if (potentialAudioSettings.startTime >= newDuration) {
          potentialAudioSettings.startTime = 0;
          potentialAudioSettings.endTime = newDuration;
        } else {
          potentialAudioSettings.endTime = Math.min(
            potentialAudioSettings.endTime,
            newDuration
          );
          if (
            potentialAudioSettings.startTime >= potentialAudioSettings.endTime
          ) {
            potentialAudioSettings.startTime = 0;
          }
        }
        adjustedMetadata.audioSettings = potentialAudioSettings;
      } else if (newFileType === "image" && oldMediaItem.type === "video") {
        adjustedMetadata.audioSettings = undefined;
      } else {
        adjustedMetadata.audioSettings = potentialAudioSettings;
      }
    }

    if (oldMediaItem?.metadata?.isCustomCover) {
      adjustedMetadata.thumbnailPath = oldMediaItem.metadata.thumbnailPath;
      adjustedMetadata.isCustomCover = true;
      adjustedMetadata.coverTimestamp = undefined;
      adjustedMetadata.coverViewMode = oldMediaItem.metadata.coverViewMode;
    }

    if (
      currentItemState?.preview &&
      currentItemState.preview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(currentItemState.preview);
    }
    const newPreviewUrl = URL.createObjectURL(processedFile);

    updateUploadItem(idToReplace, {
      preview: newPreviewUrl,
      type: processedFile.type,
      uploadProgress: 0,
      error: undefined,
      mediaItem: {
        path: "",
        type: newFileType,
        metadata: adjustedMetadata,
      },
    });
    setHasUnsavedChanges(true);

    try {
      const fileExtension = processedFile.name.split(".").pop() || "bin";
      const baseFileName = `${Date.now()}-${Math.random().toString(36).substring(2)}`;
      const newFileName = `${baseFileName}.${fileExtension}`;
      const storagePath = `posts/${activeWorkspaceId}/${newFileName}`;
      const storageRef = ref(storage, storagePath);

      if (newFileType === "video" && !adjustedMetadata.isCustomCover) {
        try {
          const videoThumbnailDataUrl =
            await generateVideoThumbnail(processedFile);
          const thumbnailFileName = `thumb-${baseFileName}.png`;
          const thumbnailStoragePath = `posts/${activeWorkspaceId}/${thumbnailFileName}`;
          const thumbnailRef = ref(storage, thumbnailStoragePath);
          const response = await fetch(videoThumbnailDataUrl);
          const thumbnailBlob = await response.blob();
          await uploadBytesResumable(thumbnailRef, thumbnailBlob);
          adjustedMetadata.thumbnailPath = thumbnailStoragePath;
        } catch (thumbError) {
          console.error(
            "Failed to generate or upload video thumbnail:",
            thumbError
          );
        }
      }

      const uploadTask = uploadBytesResumable(storageRef, processedFile);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          updateUploadItem(idToReplace, { uploadProgress: progress });
        },
        (error) => {
          console.error("Upload error during replacement:", error);
          updateUploadItem(idToReplace, {
            error: "Upload failed",
            uploadProgress: undefined,
          });
        },
        async () => {
          const downloadUrl = await getSignedUrl(storageRef.fullPath);
          updateUploadItem(idToReplace, {
            uploadProgress: 100,
            preview: downloadUrl,
            mediaItem: {
              path: storagePath,
              type: newFileType,
              metadata: {
                ...newBaseFileMetadata,
                overlays: adjustedMetadata.overlays,
                audioSettings: adjustedMetadata.audioSettings,
                thumbnailPath: adjustedMetadata.thumbnailPath,
                isCustomCover: adjustedMetadata.isCustomCover,
                coverTimestamp: adjustedMetadata.coverTimestamp,
                coverViewMode: adjustedMetadata.coverViewMode,
              },
            },
            error: undefined,
          });
          setHasUnsavedChanges(true);
        }
      );
    } catch (uploadError) {
      console.error("Error setting up upload for replacement:", uploadError);
      updateUploadItem(idToReplace, {
        error: "Failed to start upload",
        uploadProgress: undefined,
      });
    }
  };

  const handleMediaUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    if (uploadItems.length + files.length > MAX_NUM_FILES) {
      setUploadError(`Maximum ${MAX_NUM_FILES} items allowed.`);
      return;
    }
    setUploadError(null);

    const tempUploadItems: UploadItem[] = files.map((file) => ({
      id: uuidv4(),
      preview: URL.createObjectURL(file),
      type: file.type,
      uploadProgress: 0,
    }));

    if (uploadItems.length === 0) {
      setSelectedIndex(0);
    }

    editorSaveSnapshot();
    setUploadItems((prev) => [...prev, ...tempUploadItems]);
    setHasUnsavedChanges(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const tempItem = tempUploadItems[i];
      const itemId = tempItem.id;
      let processedFile = file;

      try {
        const isValidType = ALLOWED_TYPE_PATTERNS.some((pattern) =>
          pattern.test(processedFile.type)
        );
        if (!isValidType) {
          updateUploadItem(itemId, {
            error: "Invalid file type",
            uploadProgress: undefined,
          });
          continue;
        }

        if (processedFile.size > MAX_FILE_SIZE) {
          updateUploadItem(itemId, {
            error: "File too large",
            uploadProgress: undefined,
          });
          continue;
        }

        try {
          processedFile = await convertHeicToPng(processedFile);
          if (
            processedFile.type !== tempItem.type
          ) {
            const newPreview = URL.createObjectURL(processedFile);
            URL.revokeObjectURL(tempItem.preview);
            updateUploadItem(itemId, {
              preview: newPreview,
              type: processedFile.type,
            });
          }
        } catch (conversionError) {
          console.error("HEIC conversion error:", conversionError);
          updateUploadItem(itemId, {
            error: "Failed to process file (HEIC conversion).",
            uploadProgress: undefined,
          });
          continue;
        }

        const metadata = await getMediaMetadata(processedFile);
        let thumbnailPath: string | undefined;
        const fileExtension = processedFile.name.split(".").pop() || "bin";
        const baseFileName = `${Date.now()}-${Math.random().toString(36).substring(2)}`;
        const fileName = `${baseFileName}.${fileExtension}`;
        const path = `posts/${activeWorkspaceId}/${fileName}`;
        const storageRef = ref(storage, path);

        if (processedFile.type.startsWith("video")) {
          try {
            const videoThumbnailUrl =
              await generateVideoThumbnail(processedFile);
            if (videoThumbnailUrl) {
              const thumbnailFileName = `thumb-${baseFileName}.png`;
              thumbnailPath = `posts/${activeWorkspaceId}/${thumbnailFileName}`;
              const thumbnailRef = ref(storage, thumbnailPath);
              const response = await fetch(videoThumbnailUrl);
              const thumbnailBlob = await response.blob();
              await uploadBytesResumable(thumbnailRef, thumbnailBlob);
            }
          } catch (thumbError) {
            console.error("Error generating/uploading thumbnail:", thumbError);
          }
        }

        const uploadTask = uploadBytesResumable(storageRef, processedFile);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            updateUploadItem(itemId, { uploadProgress: progress });
          },
          (error) => {
            console.error("Upload error:", error);
            updateUploadItem(itemId, {
              error: "Upload failed",
              uploadProgress: undefined,
            });
          },
          async () => {
            const downloadUrl = await getSignedUrl(storageRef.fullPath);
            updateUploadItem(itemId, {
              uploadProgress: 100,
              preview: downloadUrl,
              mediaItem: {
                path,
                type: processedFile.type.startsWith("video")
                  ? "video"
                  : "image",
                metadata: thumbnailPath
                  ? { ...metadata, thumbnailPath }
                  : metadata,
              },
              error: undefined,
            });
          }
        );
      } catch (error) {
        console.error("Error processing file:", error);
        updateUploadItem(itemId, {
          error: "Processing failed",
          uploadProgress: undefined,
        });
      }
    }
    if (event.target) event.target.value = "";
  };

  const removeUploadItem = (idToRemove: string) => {
    editorSaveSnapshot();
    const oldIndex = uploadItems.findIndex((item) => item.id === idToRemove);
    const newLength = uploadItems.length - 1;

    let newSelectedIndex = selectedIndex;
    if (newLength === 0) {
      newSelectedIndex = 0;
    } else if (selectedIndex >= newLength) {
      newSelectedIndex = newLength - 1;
    } else if (oldIndex === selectedIndex && selectedIndex > 0) {
      newSelectedIndex = Math.max(0, selectedIndex - 1);
    } else if (oldIndex < selectedIndex) {
      newSelectedIndex = selectedIndex - 1;
    }

    setUploadItems((prev) => {
      const itemToRemove = prev.find((item) => item.id === idToRemove);
      if (itemToRemove?.preview && itemToRemove.preview.startsWith("blob:")) {
        URL.revokeObjectURL(itemToRemove.preview);
      }
      return prev.filter((item) => item.id !== idToRemove);
    });
    setSelectedIndex(newSelectedIndex);
    setHasUnsavedChanges(true);
  };

  const reorderUploadItems = (dragIndex: number, dropIndex: number) => {
    editorSaveSnapshot();
    setUploadItems((prev) => {
      const newItems = [...prev];
      const [draggedItem] = newItems.splice(dragIndex, 1);
      newItems.splice(dropIndex, 0, draggedItem);
      return newItems;
    });
    setSelectedIndex(dropIndex);
    setHasUnsavedChanges(true);
  };

  const handleDuplicateMediaItem = (idToDuplicate: string) => {
    setUploadItems((prev) => {
      const itemIndex = prev.findIndex((item) => item.id === idToDuplicate);
      if (itemIndex === -1) {
        return prev;
      }
      const itemToDuplicate = prev[itemIndex];
      const duplicatedItem: UploadItem = {
        ...itemToDuplicate,
        id: uuidv4(),
      };

      const newItems = [...prev];
      newItems.splice(itemIndex + 1, 0, duplicatedItem);
      return newItems;
    });
    setHasUnsavedChanges(true);
  };

  // Save the in-progress post (so a connect round-trip doesn't lose work) and
  // stash its id, so /settings can offer a "Continue your post" link after the
  // user connects. Shared by every "leave to connect an account" entry point.
  const saveDraftAndStashReturn = useCallback(async (intent?: string) => {
    let returnDraftId = draftId;
    const hasContent =
      hasUnsavedChanges ||
      Boolean(commonSettings.description) ||
      Boolean(commonSettings.title) ||
      uploadItems.length > 0;
    // Only persist when there's something worth returning to — avoids creating
    // empty drafts when a user connects from a blank editor.
    if (draftId || hasContent) {
      setIsSaving(true);
      try {
        const saved = await saveDraft();
        if (saved) {
          returnDraftId = saved;
          if (!draftId) setDraftId(saved);
        }
      } catch (err) {
        console.error("Failed to save draft before connect:", err);
      } finally {
        setIsSaving(false);
      }
    }
    if (returnDraftId) stashReturnToPost(returnDraftId, intent);
    return returnDraftId;
  }, [
    draftId,
    hasUnsavedChanges,
    commonSettings.description,
    commonSettings.title,
    uploadItems.length,
    saveDraft,
  ]);

  // Connect from the publish modal: save + stash, then OAuth. Coming back
  // reopens the modal (intent publish-now) with the new channel ticked.
  const handleConnectFromPost = useCallback(
    async (platform: PlatformType) => {
      const uid = activeWorkspaceId;
      if (!uid || !currentUser) return;
      // Come back to the same mode: scheduling stays scheduling after OAuth.
      const returnIntent = publishStartScheduling ? "schedule" : "publish-now";
      const returnDraftId = await saveDraftAndStashReturn(returnIntent);
      // Intentional navigation — don't trip the unsaved-changes prompt.
      suppressUnloadPromptRef.current = true;
      // The server sends the callback straight back here; the stashed hint
      // above is only Settings' fallback.
      await beginWorkspaceOAuth(
        currentUser,
        uid,
        platform,
        returnDraftId
          ? { returnPath: "/create-post", returnDraftId, returnIntent }
          : undefined
      );
    },
    [activeWorkspaceId, currentUser, saveDraftAndStashReturn, publishStartScheduling]
  );

  const prepareMediaForProcessing = async (): Promise<{
    draftId: string;
    processedMediaItems: PostMediaItem[];
  }> => {
    if (!activeWorkspaceId) {
      throw new Error("User not authenticated");
    }

    let currentDraftIdForProcessing = draftId;

    // Step A: Ensure Draft ID and Initial Save
    if (!currentDraftIdForProcessing) {
      console.log("[PrepareMedia] No draftId found, attempting initial save.");
      const initialSavedDraftId = await saveDraft();
      if (!initialSavedDraftId) {
        throw new Error(
          "Failed to create or secure a draft ID before generating overlay images."
        );
      }
      currentDraftIdForProcessing = initialSavedDraftId;
      setDraftId(currentDraftIdForProcessing);
      setHasUnsavedChanges(false);
      console.log(
        `[PrepareMedia] Initial save successful. Draft ID: ${currentDraftIdForProcessing}`
      );
    } else {
      // If a draftId already exists, and there are unsaved changes, save them first.
      if (hasUnsavedChanges) {
        console.log(
          `[PrepareMedia] Existing draftId ${currentDraftIdForProcessing} with unsaved changes, saving before overlay generation.`
        );
        const preSaveId = await saveDraft({
          draftIdOverride: currentDraftIdForProcessing,
        });
        if (!preSaveId) {
          throw new Error(
            "Failed to save existing draft with unsaved changes before overlay generation."
          );
        }
        setHasUnsavedChanges(false);
      }
    }

    // Note: Overlay image pre-rendering removed - server-side Remotion handles overlay rendering
    // Step B: Final Save
    console.log(
      `[PrepareMedia] Performing final save for draft ID: ${currentDraftIdForProcessing}`
    );
    const finalProcessedMediaItems = compact(
      uploadItems.map((item) => item.mediaItem)
    );
    const finalSavedDraftId = await saveDraft({
      mediaItemsOverride: finalProcessedMediaItems,
      draftIdOverride: currentDraftIdForProcessing,
    });

    if (!finalSavedDraftId) {
      throw new Error("Failed to save draft before processing.");
    }

    if (finalSavedDraftId !== currentDraftIdForProcessing) {
      throw new Error("Draft ID mismatch after saving.");
    }
    setHasUnsavedChanges(false);
    console.log(
      `[PrepareMedia] Final save successful. Draft ID: ${finalSavedDraftId}`
    );

    return {
      draftId: finalSavedDraftId,
      processedMediaItems: finalProcessedMediaItems,
    };
  };

  const cancelSchedule = async () => {
    if (!activeWorkspaceId || !currentUser || !draftId || isCancelingSchedule) return;

    setIsCancelingSchedule(true);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/schedule/cancel`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ postId: draftId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to cancel schedule");
      }

      // Clear local state
      setConnectionSchedules({});
      setCommonSettings((prev) => ({ ...prev, scheduledTime: "" }));
      setIsScheduled(false);

      navigate("/dashboard");
    } catch (error) {
      console.error("Error canceling schedule:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to cancel schedule. Please try again.",
      });
    } finally {
      setIsCancelingSchedule(false);
    }
  };

  const publishPost = async (publishImmediately?: boolean) => {
    if (!activeWorkspaceId || isPublishing) return;

    // Check if user is on free plan and content has watermark flag
    const hasWatermarkContent = uploadItems.some(
      (item) => item.mediaItem?.metadata?.generatedWithWatermark
    );

    if (isFreePlan && hasWatermarkContent) {
      // Show watermark confirmation dialog
      setWatermarkDialogAction("publish");
      setPendingAction(() => () => performPublish(publishImmediately));
      setIsWatermarkDialogOpen(true);
      return;
    }

    // YouTube converts carousels to a video slideshow at publish time —
    // nudge toward a preview when no rendition exists yet. Desktop only:
    // mobile has no drill-down preview to offer. At most one interruption
    // per publish (the watermark dialog wins above).
    const youtubeConversionSelected =
      !isMobile &&
      !hasVideoPostOutput &&
      !!draftId &&
      uploadItems.length > 0 &&
      uploadItems.every((item) => item.type?.startsWith("image")) &&
      Array.from(selectedConnections).some(
        (id) => connections.find((c) => c.id === id)?.platform === "youtube"
      );
    if (youtubeConversionSelected) {
      // Live check: originalPost is a load-time snapshot, but the rendition
      // may have been written since — by a preview (this session or any
      // other) or a prior publish. Firestore is the source of truth.
      try {
        const { doc, getDoc } = await import("firebase/firestore");
        const snap = await getDoc(doc(db, "posts", draftId));
        if (!snap.data()?.videoChoreography) {
          setPendingAction(() => () => performPublish(publishImmediately));
          setYoutubeNudgeOpen(true);
          return;
        }
      } catch {
        // If the check fails, don't let a nudge block publishing
      }
    }

    // If not free plan or no watermark, proceed with publish
    await performPublish(publishImmediately);
  };

  const performPublish = async (publishImmediately?: boolean) => {
    if (!activeWorkspaceId || isPublishing) return;

    if (hasCriticalMediaErrors) {
      const problematicItems = uploadItems
        .filter(
          (item) =>
            item.error ||
            (item.uploadProgress !== undefined && item.uploadProgress < 100)
        )
        .map((item) => {
          try {
            const url = new URL(item.preview);
            const parts = decodeURIComponent(url.pathname).split("/");
            return (
              parts[parts.length - 1] || `Item ${uploadItems.indexOf(item) + 1}`
            );
          } catch {
            return `Item ${uploadItems.indexOf(item) + 1}`;
          }
        });

      toast({
        variant: "destructive",
        title: "Cannot Publish Post",
        description: `The following media items have errors or are still uploading: ${problematicItems.join(
          ", "
        )}. Please remove or replace them, or wait for uploads to complete.`,
        duration: 7000,
      });
      return;
    }

    const validMediaToPublish = uploadItems.filter(
      (item) => item.mediaItem && !item.error && item.uploadProgress === 100
    );
    if (uploadItems.length > 0 && validMediaToPublish.length === 0) {
      toast({
        variant: "destructive",
        title: "No Valid Media for Publishing",
        description:
          "All selected media items have issues or are not fully processed. Please add or fix your media to publish.",
        duration: 7000,
      });
      return;
    }

    setIsPublishing(true);

    try {
      // Video settings save optimistically, but publishing must read the
      // acknowledged Firestore state rather than racing the last tap.
      await awaitPendingVideoSettingsSave();

      // Use shared media preparation logic
      const { draftId: currentDraftIdForPublish } =
        await prepareMediaForProcessing();

      // Step D: Publish
      console.log(
        `[Publishing] Publishing post with ID: ${currentDraftIdForPublish}`
      );

      if (currentUser && activeWorkspaceId) {
        void trackPublishAttempt(currentUser, activeWorkspaceId, originalPost,
          isScheduled && !publishImmediately ? "schedule" : "now");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/schedule`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${await currentUser?.getIdToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            postId: currentDraftIdForPublish,
            scheduledFor:
              isScheduled && !publishImmediately
                ? new Date(commonSettings.scheduledTime).getTime()
                : 0,
            ...(isScheduled &&
              !publishImmediately &&
              Object.keys(connectionSchedules).length > 0 && {
                connectionSchedules: Object.fromEntries(
                  Object.entries(connectionSchedules).map(([connId, data]) => [
                    connId,
                    data.scheduledFor,
                  ])
                ),
              }),
          }),
        }
      );

      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || errorData.error || errorMsg;
        } catch (e) {
          // Ignore if response is not JSON
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();
      if (result.status === "done") {
        toast({
          title: result.isUpdate ? "Post updated" : "Post scheduled",
          description: result.isUpdate
            ? "Your post has been updated successfully."
            : "Your post has been scheduled successfully.",
        });
        navigate("/dashboard");
      } else {
        throw new Error(
          result.message || "Publishing did not complete successfully."
        );
      }
    } catch (error) {
      console.error("Error publishing post:", error);
      toast({
        variant: "destructive",
        title: "Error Publishing Post",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const exportMedia = async (
    exportFormat: "individual" | "pdf" = "individual"
  ) => {
    if (!activeWorkspaceId || isExporting) return;

    // Check if user is on free plan and content has watermark flag
    const hasWatermarkContent = uploadItems.some(
      (item) => item.mediaItem?.metadata?.generatedWithWatermark
    );

    if (isFreePlan && hasWatermarkContent) {
      // Show watermark confirmation dialog
      setWatermarkDialogAction("export");
      setPendingAction(() => () => performExport(exportFormat));
      setIsWatermarkDialogOpen(true);
      return;
    }

    // If not free plan or no watermark, proceed with export
    await performExport(exportFormat);
  };

  const performExport = async (
    exportFormat: "individual" | "pdf" = "individual"
  ) => {
    if (!activeWorkspaceId || isExporting) return;

    // Check if there are valid media items to export
    const validMediaToExport = uploadItems.filter(
      (item) => item.mediaItem && !item.error && item.uploadProgress === 100
    );

    if (uploadItems.length > 0 && validMediaToExport.length === 0) {
      toast({
        variant: "destructive",
        title: "No Valid Media for Export",
        description:
          "All media items have issues or are not fully processed. Please fix your media before exporting.",
      });
      return;
    }

    if (validMediaToExport.length === 0) {
      toast({
        variant: "destructive",
        title: "No Media to Export",
        description: "Please add media items before exporting.",
      });
      return;
    }

    // Immediately show loading dialog
    setIsExporting(true);
    setIsExportLoadingModalOpen(true);

    // Yield to the browser so the loading modal renders and the spinner animation starts
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(() => r(undefined))));

    try {
      // Use shared media preparation logic
      const { draftId: currentDraftIdForExport } =
        await prepareMediaForProcessing();

      // Generate post title for PDF naming (convert to snake_case)
      const postTitle =
        commonSettings.title || commonSettings.description || "carousel";
      const snakeCaseTitle =
        postTitle
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_") // Replace non-alphanumeric with underscore
          .replace(/_+/g, "_") // Replace multiple underscores with single
          .replace(/^_+|_+$/g, "") // Remove leading/trailing underscores
          .substring(0, 40) || "carousel"; // Limit to 40 chars, fallback if empty

      // Step D: Process media for export
      // Check if this is a video post
      const isVideoExport = !!liveVideoChoreography;

      console.log(
        `[Export] Processing media for export (format: ${isVideoExport ? 'video' : exportFormat})...`
      );

      let result: any;

      if (isVideoExport) {
        // Video export: render MP4 via Remotion
        const videoResponse = await fetch(
          `${process.env.REACT_APP_API_BASE}/export/carousel-video`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${await currentUser?.getIdToken()}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              postId: currentDraftIdForExport,
            }),
          }
        );

        if (!videoResponse.ok) {
          let errorMsg = `HTTP error! status: ${videoResponse.status}`;
          try {
            const errorData = await videoResponse.json();
            errorMsg = errorData.message || errorData.error || errorMsg;
          } catch (e) {}
          throw new Error(errorMsg);
        }

        result = await videoResponse.json();

        // Close loading dialog
        setIsExportLoadingModalOpen(false);

        setExportResults(result);
        setExportTitle(snakeCaseTitle);
      } else {
        // Carousel export: existing flow
        const response = await fetch(`${process.env.REACT_APP_API_BASE}/export`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${await currentUser?.getIdToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            postId: currentDraftIdForExport,
            exportFormat,
            postTitle: snakeCaseTitle,
          }),
        });

        if (!response.ok) {
          let errorMsg = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMsg = errorData.message || errorData.error || errorMsg;
          } catch (e) {}
          throw new Error(errorMsg);
        }

        result = await response.json();

        // Close loading dialog
        setIsExportLoadingModalOpen(false);

        // Store results and title for the modal
        setExportResults(result);
        setExportTitle(snakeCaseTitle);
      }

      // Auto-download the prepared export. The results modal keeps a manual retry
      // available in case the browser blocks the automatic download handoff.
      try {
        await downloadExportResult(result, snakeCaseTitle);
      } catch (dlError) {
        console.error("Auto-download failed:", dlError);
      }

      // Show results modal
      setIsExportModalOpen(true);
    } catch (error) {
      console.error("Error exporting media:", error);

      // Close loading dialog on error
      setIsExportLoadingModalOpen(false);

      toast({
        variant: "destructive",
        title: "Export Failed",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadExportAgain = useCallback(async () => {
    if (!exportResults || !exportTitle) return;

    try {
      await downloadExportResult(exportResults, exportTitle);
    } catch (error) {
      console.error("Download retry failed:", error);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description:
          error instanceof Error
            ? error.message
            : "The export completed, but the browser could not start the download.",
      });
    }
  }, [exportResults, exportTitle, toast]);

  const handleMediaUpdate = (updatedMediaItemsFromEditor: UploadItem[]) => {
    setUploadItems(updatedMediaItemsFromEditor);
    setHasUnsavedChanges(true);
    setMediaUpdateCounter((prev) => prev + 1);
    console.log(
      "[CreatePost] Media items updated from MediaEditor:",
      updatedMediaItemsFromEditor
    );
  };

  // Undo/redo for the image editor
  const restoreEditorUndoState = useCallback((snapshot: UploadItem[]) => {
    setUploadItems(snapshot);
    setHasUnsavedChanges(true);
  }, []);

  const {
    saveSnapshot: editorSaveSnapshot,
    undo: editorUndo,
    redo: editorRedo,
    canUndo: editorCanUndo,
    canRedo: editorCanRedo,
  } = useUndoHistory(uploadItems, restoreEditorUndoState);

  const handleOverlayChange = useCallback((newOverlays: AnyOverlay[]) => {
    setHasUnsavedChanges(true);
    setUploadItems((prev) => {
      const newItems = [...prev];
      if (newItems[selectedIndex] && newItems[selectedIndex].mediaItem) {
        newItems[selectedIndex] = {
          ...newItems[selectedIndex],
          mediaItem: {
            ...newItems[selectedIndex].mediaItem!,
            metadata: {
              ...newItems[selectedIndex].mediaItem!.metadata,
              overlays: newOverlays,
            },
          },
        };
      }
      return newItems;
    });
  }, [selectedIndex]);

  const handleBackgroundChange = useCallback((
    path: string,
    metadata: Pick<PostMediaItem["metadata"], "dimensions" | "size">
  ) => {
    editorSaveSnapshot();
    setHasUnsavedChanges(true);
    setUploadItems((prev) => {
      const newItems = [...prev];
      if (newItems[selectedIndex] && newItems[selectedIndex].mediaItem) {
        const item = newItems[selectedIndex];
        const nextMetadata = { ...item.mediaItem!.metadata };
        delete nextMetadata.backgroundColor;

        // Optimistically update path
        newItems[selectedIndex] = {
          ...item,
          mediaItem: {
            ...item.mediaItem!,
            path: path,
            metadata: {
              ...nextMetadata,
              dimensions: metadata.dimensions,
              size: metadata.size,
            },
          },
        };

        // Update preview URL
        if (path.startsWith("posts/")) {
             getSignedUrl(path).then(url => {
                 setUploadItems(current => current.map((i, idx) =>
                    idx === selectedIndex ? { ...i, preview: url } : i
                 ));
             });
        } else {
             newItems[selectedIndex].preview = path;
        }
      }
      return newItems;
    });
  }, [selectedIndex, editorSaveSnapshot]);

  const handleBackgroundColorChange = useCallback((
    backgroundColor: string,
    path: string,
    metadata: Pick<PostMediaItem["metadata"], "dimensions" | "size">
  ) => {
    editorSaveSnapshot();
    setHasUnsavedChanges(true);
    setUploadItems((prev) => {
      const newItems = [...prev];
      if (newItems[selectedIndex] && newItems[selectedIndex].mediaItem) {
        const item = newItems[selectedIndex];
        const nextMetadata = { ...item.mediaItem!.metadata };
        delete nextMetadata.backgroundFilter;

        newItems[selectedIndex] = {
          ...item,
          mediaItem: {
            ...item.mediaItem!,
            path: path,
            metadata: {
              ...nextMetadata,
              dimensions: metadata.dimensions,
              size: metadata.size,
              backgroundColor,
            },
          },
        };

        if (path.startsWith("posts/")) {
          getSignedUrl(path).then(url => {
            setUploadItems(current => current.map((i, idx) =>
              idx === selectedIndex ? { ...i, preview: url } : i
            ));
          });
        }
      }
      return newItems;
    });
  }, [selectedIndex, editorSaveSnapshot]);

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // --- Carousel Video Preview state ---
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [videoChoreography, setVideoChoreography] = useState<VideoChoreography | null>(null);
  const [isLoadingChoreography, setIsLoadingChoreography] = useState(false);
  const [videoMusicPath, setVideoMusicPath] = useState<string | undefined>();
  const [videoMusicUrl, setVideoMusicUrl] = useState<string | undefined>();
  const [videoAudioMuted, setVideoAudioMuted] = useState(!!originalPost?.videoAudioMuted);
  const [videoSpeed, setVideoSpeed] = useState(1);
  const [isRevertingToCarousel, setIsRevertingToCarousel] = useState(false);
  const [isSavingVideoSettings, setIsSavingVideoSettings] = useState(false);
  const [isEditorVideoMutationPending, setIsEditorVideoMutationPending] =
    useState(false);
  const videoSettingsSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const videoSettingsSnapshotRef = useRef<Record<string, unknown>>({});
  // Music, mute and speed the editor has saved to this draft since it loaded.
  // originalPost doesn't see those writes, and the publish modal's preview
  // mounts a fresh editor that starts from these saved values.
  const [savedVideoSettingsEdits, setSavedVideoSettingsEdits] = useState<
    Pick<Partial<Post>, "videoMusicPath" | "videoAudioMuted" | "videoSpeed">
  >({});

  const persistVideoSettings = useCallback(
    (fields: Record<string, unknown>): Promise<void> => {
      if (!draftId) {
        return Promise.reject(new Error("Save the post before changing video settings"));
      }

      videoSettingsSnapshotRef.current = {
        ...videoSettingsSnapshotRef.current,
        ...fields,
      };
      const snapshot = { ...videoSettingsSnapshotRef.current };
      const save = videoSettingsSaveQueueRef.current
        .catch(() => undefined)
        .then(async () => {
          const { updateDoc } = await import("firebase/firestore");
          await updateDoc(doc(db, "posts", draftId), {
            ...snapshot,
            updatedAt: Date.now(),
          });
        });

      videoSettingsSaveQueueRef.current = save;
      setSavedVideoSettingsEdits((prev) => ({ ...prev, ...fields }));
      setIsSavingVideoSettings(true);
      const finish = () => {
        if (videoSettingsSaveQueueRef.current === save) {
          setIsSavingVideoSettings(false);
        }
      };
      void save.then(finish, finish);
      return save;
    },
    [draftId]
  );

  const awaitPendingVideoSettingsSave = useCallback(async () => {
    await videoSettingsSaveQueueRef.current;
  }, []);

  const settlePendingVideoSettingsSave = useCallback(async () => {
    try {
      await videoSettingsSaveQueueRef.current;
    } catch {
      // The settings control already surfaced the failure. Revert should still
      // win after the failed write has settled.
    }
  }, []);

  const clearVideoSettingsSaveQueue = useCallback(() => {
    videoSettingsSnapshotRef.current = {};
    videoSettingsSaveQueueRef.current = Promise.resolve();
    setIsSavingVideoSettings(false);
  }, []);

  const isVideoMutationPending =
    isLoadingChoreography ||
    isRevertingToCarousel ||
    isSavingVideoSettings ||
    isEditorVideoMutationPending;
  const areVideoSettingsLocked =
    isSaving ||
    shareLoading ||
    isPublishing ||
    isExporting ||
    isLoadingChoreography ||
    isRevertingToCarousel ||
    isEditorVideoMutationPending;

  // Live choreography from the editor — mirrors originalPost on load and updates
  // when the user converts/reverts inside ImageCompositeEditor (which writes to
  // Firestore directly without round-tripping through originalPost).
  // Gated on isVideoPost: a choreography cached as a per-platform rendition
  // (videoEnabled=false) must not put the post into video mode.
  const originalVideoChoreography =
    originalPost && isVideoPostEnabled(originalPost)
      ? originalPost.videoChoreography
      : undefined;
  const [liveVideoChoreography, setLiveVideoChoreography] = useState<VideoChoreography | null>(
    originalVideoChoreography ?? null
  );
  const previousVideoDraftIdRef = useRef<string | null>(draftId);

  useEffect(() => {
    if (previousVideoDraftIdRef.current === draftId) return;
    previousVideoDraftIdRef.current = draftId;

    clearVideoSettingsSaveQueue();
    setSavedVideoSettingsEdits({});
    setShowVideoPreview(false);
    setVideoChoreography(null);
    setLiveVideoChoreography(null);
    setVideoMusicPath(undefined);
    setVideoMusicUrl(undefined);
    setVideoAudioMuted(false);
    setVideoSpeed(1);
  }, [clearVideoSettingsSaveQueue, draftId]);

  useEffect(() => {
    setLiveVideoChoreography(originalVideoChoreography ?? null);
  }, [originalVideoChoreography]);
  const hasVideoPostOutput = !!liveVideoChoreography;

  // Hydrate every mobile video field, including absent/default values, so a
  // previous draft can never leak music, speed, or preview mode into this one.
  useEffect(() => {
    if (!isMobile) return;
    setShowVideoPreview(!!originalVideoChoreography);
    setVideoChoreography(originalVideoChoreography ?? null);
    setVideoMusicPath(originalPost?.videoMusicPath);
    setVideoAudioMuted(!!originalPost?.videoAudioMuted);
    setVideoSpeed(originalPost?.videoSpeed ?? 1);
  }, [
    isMobile,
    originalPost?.id,
    originalPost?.videoAudioMuted,
    originalPost?.videoMusicPath,
    originalPost?.videoSpeed,
    originalVideoChoreography,
  ]);

  // Resolve music signed URL when path changes
  useEffect(() => {
    if (videoAudioMuted || !videoMusicPath) {
      setVideoMusicUrl(undefined);
      return;
    }
    let cancelled = false;
    setVideoMusicUrl(undefined);
    resolveMusicUrl(videoMusicPath, currentUser).then((url) => {
      if (!cancelled) setVideoMusicUrl(url);
    }).catch((error) => {
      console.warn("Failed to resolve video music URL:", error);
      if (!cancelled) setVideoMusicUrl(undefined);
    });
    return () => { cancelled = true; };
  }, [currentUser, videoAudioMuted, videoMusicPath]);

  // Apply speed adjustment to choreography
  // Plays as published: scaled by speed, then cut on the music's beat
  const beatGrid = useMusicBeatGrid(videoMusicPath, videoAudioMuted, currentUser);
  const speedAdjustedChoreography = useMemo(
    () => (videoChoreography ? timeChoreography(videoChoreography, videoSpeed, beatGrid) : videoChoreography),
    [videoChoreography, videoSpeed, beatGrid]
  );

  const videoPreviewSlides: SlideData[] = useMemo(() => {
    if (!showVideoPreview) return [];
    return uploadItems
      .filter((item) => item.type?.startsWith("image") && item.mediaItem)
      .map((item) => {
        const mediaItem = item.mediaItem!;
        const overlays = mediaItem.metadata?.overlays ?? [];
        return {
          overlays,
          backgroundImageUrl: item.preview,
          backgroundColor: mediaItem.metadata?.backgroundColor,
          backgroundFilter: mediaItem.metadata?.backgroundFilter,
          dimensions: mediaItem.metadata?.dimensions ?? { width: 1080, height: 1350 },
          imageUrls: overlayImageUrlCache,
        };
      });
  }, [showVideoPreview, uploadItems, overlayImageUrlCache]);

  const handleMobileConvertToVideo = async () => {
    const imageCount = uploadItems.filter(
      (item) => item.type?.startsWith("image") && item.mediaItem
    ).length;
    if (imageCount < 1) return;

    if (!currentUser) {
      toast({
        variant: "destructive",
        title: "Couldn't create video",
        description: "Sign in, then try again.",
      });
      return;
    }

    // Start the small metadata request while choreography is generating so
    // the music panel is ready by the time the video preview appears.
    void prefetchMusicLibrary(currentUser).catch(() => {});

    posthog.capture("mobile_video_conversion_started", {
      slide_count: imageCount,
    });
    setIsLoadingChoreography(true);
    try {
      // The server choreographs the persisted post. Flush recent uploads or
      // edits first so a quick tap never converts a stale set of slides.
      let conversionPostId = draftId;
      if (!conversionPostId || hasUnsavedChanges) {
        conversionPostId = await saveDraft();
        if (conversionPostId) {
          if (!draftId) setDraftId(conversionPostId);
          setHasUnsavedChanges(false);
        }
      }
      if (!conversionPostId) {
        throw new Error("Post could not be saved before conversion");
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/carousel-video/choreography`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ postId: conversionPostId }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success || !data.choreography) {
        throw new Error(data.message || data.error || "Failed to create video");
      }

      setVideoChoreography(data.choreography);
      setLiveVideoChoreography(data.choreography);
      if (data.music?.musicPath) {
        setVideoMusicPath(data.music.musicPath);
        setSavedVideoSettingsEdits((prev) => ({
          ...prev,
          videoMusicPath: data.music.musicPath,
        }));
      }
      setShowVideoPreview(true);
      posthog.capture("mobile_video_conversion_succeeded", {
        slide_count: imageCount,
        used_cached_choreography: data.fromLLM === false,
      });
    } catch (error) {
      console.error("Failed to generate choreography:", error);
      posthog.capture("mobile_video_conversion_failed", {
        slide_count: imageCount,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      toast({
        variant: "destructive",
        title: "Couldn't create video",
        description: "We couldn't finish the conversion. Please try again.",
      });
    } finally {
      setIsLoadingChoreography(false);
    }
  };

  const handleMobileRevertToCarousel = async () => {
    if (!draftId) {
      toast({
        variant: "destructive",
        title: "Couldn't switch format",
        description: "Save the post, then try again.",
      });
      return;
    }

    setIsRevertingToCarousel(true);
    try {
      await settlePendingVideoSettingsSave();
      const { updateDoc } = await import("firebase/firestore");
      await updateDoc(doc(db, "posts", draftId), {
        videoEnabled: false,
        updatedAt: Date.now(),
      });

      setShowVideoPreview(false);
      setLiveVideoChoreography(null);
      clearVideoSettingsSaveQueue();
      posthog.capture("mobile_video_reverted_to_carousel");
    } catch (error) {
      console.error("Failed to revert video to carousel:", error);
      toast({
        variant: "destructive",
        title: "Couldn't switch format",
        description: "Your format hasn't changed. Please try again.",
      });
    } finally {
      setIsRevertingToCarousel(false);
    }
  };

  const hasStartedCreating = useMemo(() => {
    return (
      uploadItems.length > 0 ||
      Boolean(commonSettings.description.trim()) ||
      commonSettings.scheduledTime !== "" ||
      selectedConnections.size > 0
    );
  }, [
    uploadItems.length,
    commonSettings.description,
    commonSettings.scheduledTime,
    selectedConnections.size,
  ]);

  // handleGenerateFromUrl and handleGenerateFromText moved to GeneratePage

  // Clamp to valid range so an intermediate render never accesses out-of-bounds.
  const safeSelectedIndex = Math.min(selectedIndex, Math.max(uploadItems.length - 1, 0));

  const isPreviewPanelVisible = useMemo(() => {
    return (
      uploadItems.length > 0 &&
      selectedIndex < uploadItems.length &&
      uploadItems[safeSelectedIndex]
    );
  }, [uploadItems, selectedIndex, safeSelectedIndex]);

  // Don't pass broken preview URLs to canvas — Remotion's <Img> calls cancelRender on error.
  const selectedItemPreviewUrl = useMemo(() => {
    const item = uploadItems[safeSelectedIndex];
    if (!item || item.error || (item.uploadProgress !== undefined && item.uploadProgress < 100)) {
      return undefined;
    }
    return item.preview;
  }, [uploadItems, safeSelectedIndex]);

  // Keep preview URL map scoped to the selected item to avoid unrelated rerenders.
  const selectedOverlayPreviewUrls = useMemo(() => {
    const overlays = uploadItems[safeSelectedIndex]?.mediaItem?.metadata?.overlays || [];
    const urls: Record<string, string> = {};

    for (const overlay of overlays) {
      if (
        overlay.type === "image" &&
        "filePath" in overlay &&
        overlay.filePath
      ) {
        const cachedUrl = overlayImageUrlCache[overlay.filePath];
        if (cachedUrl) {
          urls[overlay.filePath] = cachedUrl;
        }
      }
    }

    return urls;
  }, [uploadItems, safeSelectedIndex, overlayImageUrlCache]);

  const mobileCarouselPreviewStyle = useMemo(
    () =>
      getMobilePreviewStyle(
        getPreviewAspectSizing(
          uploadItems[safeSelectedIndex]?.mediaItem?.metadata?.dimensions
        )
      ),
    [uploadItems, safeSelectedIndex]
  );
  const mobileVideoPreviewStyle = useMemo(
    () =>
      getMobilePreviewStyle({
        aspectRatio: "9 / 16",
        widthToHeightRatio: 9 / 16,
      }),
    []
  );

  // Keep editor indices aligned with uploadItems while some items are still initializing.
  const studioEditorMedia = useMemo(() => {
    const mediaItems: PostMediaItem[] = [];
    const uploadIndexByEditorIndex: number[] = [];

    uploadItems.forEach((item, uploadIndex) => {
      if (item.mediaItem) {
        mediaItems.push(item.mediaItem);
        uploadIndexByEditorIndex.push(uploadIndex);
      }
    });

    return { mediaItems, uploadIndexByEditorIndex };
  }, [uploadItems]);

  const selectedStudioEditorIndex = useMemo(
    () => studioEditorMedia.uploadIndexByEditorIndex.indexOf(safeSelectedIndex),
    [studioEditorMedia, safeSelectedIndex]
  );

  // Check if all valid media items are images (for PDF export option)
  // Only show PDF option for 2+ images (single image PDF doesn't make sense)
  const allMediaItemsAreImages = useMemo(() => {
    const validItems = uploadItems.filter(
      (item) => item.mediaItem && !item.error && item.uploadProgress === 100
    );
    if (validItems.length < 2) return false; // Need at least 2 images for PDF
    return validItems.every((item) => item.mediaItem?.type === "image");
  }, [uploadItems]);

  // Video conversion accepts one or more ready image slides. Keep this
  // separate from the PDF export rule above, which intentionally requires 2+.
  const canConvertCarouselToVideo = useMemo(() => {
    const readyItems = uploadItems.filter(
      (item) => item.mediaItem && !item.error && item.uploadProgress === 100
    );
    return (
      readyItems.length >= 1 &&
      readyItems.length === uploadItems.length &&
      readyItems.every((item) => item.mediaItem?.type === "image")
    );
  }, [uploadItems]);

  // Export gate: free users with watermarked content can't download. Surface an
  // "Upgrade" pill on the Export button so the premium nature is clear upfront.
  const exportShowsUpgrade =
    EXPORT_GATE_ENABLED &&
    isFreePlan &&
    uploadItems.some(
      (item) => item.mediaItem?.metadata?.generatedWithWatermark
    );

  // Whether a schedule is currently set (either per-connection or single time)
  // Everything already on the calendar, so the schedule picker can show the user what a
  // day already holds instead of making them bounce to /calendar to find out.
  const { posts: scheduledPosts } = useScheduledPosts();
  const scheduleByDate = useMemo(
    () => buildScheduleByDate(scheduledPosts, { excludePostId: originalPost?.id }),
    [scheduledPosts, originalPost?.id]
  );
  const connectionsById = useMemo(
    () => Object.fromEntries(connections.map((conn) => [conn.id, conn])),
    [connections]
  );


  // Human-readable schedule summary for the top bar
  const scheduleSummaryLabel = useMemo(() => {
    const perConnCount = Object.keys(connectionSchedules).length;
    if (perConnCount > 0) {
      if (perConnCount === 1) {
        const time = Object.values(connectionSchedules)[0].scheduledFor;
        return new Date(time).toLocaleString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }
      const times = Object.values(connectionSchedules).map((c) => c.scheduledFor).sort((a, b) => a - b);
      const fmt = (t: number) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const earliest = fmt(times[0]);
      const latest = fmt(times[times.length - 1]);
      return `${perConnCount} channels, ${earliest === latest ? earliest : `${earliest}–${latest}`}`;
    }
    if (commonSettings.scheduledTime) {
      return new Date(commonSettings.scheduledTime).toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    return "";
  }, [connectionSchedules, commonSettings.scheduledTime]);

  // Remove per-account suggestions for deselected accounts (but not when loading from draft)
  useEffect(() => {
    if (isPopulatingFromDraft.current) return;
    setConnectionSchedules((prev) => {
      const filtered = Object.fromEntries(
        Object.entries(prev).filter(([connId]) => selectedConnections.has(connId))
      );
      // Only update if something was actually removed
      if (Object.keys(filtered).length === Object.keys(prev).length) return prev;
      return filtered;
    });
  }, [selectedConnections]);


  const handleSelectItem = useCallback(
    (index: number) => {
      setSelectedIndex(index);
      // Auto-retry loading preview for items that previously failed
      const item = uploadItems[index];
      if (item?.error && item.mediaItem?.path) {
        const path = item.mediaItem.path;
        if (path && (!path.startsWith("http") || isFirebaseStorageUrl(path))) {
          // Show spinner while retrying
          setUploadItems((current) =>
            current.map((it, idx) =>
              idx === index
                ? { ...it, error: undefined, uploadProgress: 0 }
                : it
            )
          );
          getSignedUrl(path)
            .then((url) => {
              setMediaUnreachable(false);
              setUploadItems((current) =>
                current.map((it, idx) =>
                  idx === index
                    ? { ...it, preview: url, uploadProgress: 100 }
                    : it
                )
              );
            })
            .catch((err) => {
              console.warn(
                `[CreatePost] Retry failed for media item: ${path}`,
                err
              );
              if (isFirebaseNetworkError(err)) setMediaUnreachable(true);
              setUploadItems((current) =>
                current.map((it, idx) =>
                  idx === index
                    ? { ...it, error: "Failed to load media", uploadProgress: 100 }
                    : it
                )
              );
            });
        }
      }
    },
    [uploadItems]
  );

  const openMediaEditor = (index: number) => {
    handleSelectItem(index);
    setIsMediaEditorOpen(true);
  };

  // ---- Publish modal ------------------------------------------------------

  // What posts as the first comment when a channel turns it on and nobody
  // typed one: a link back to the source (the first, for combined sources).
  const defaultFirstCommentLink = getFirstUrlFromGenerationSource(
    originalPost?.generationSource
  );
  const defaultFirstComment = defaultFirstCommentLink
    ? `More details at: ${defaultFirstCommentLink}`
    : "";

  defaultFirstCommentRef.current = defaultFirstComment;

  const hasPublishableMedia = uploadItems.some(
    (item) => item.type?.startsWith("image") || item.type?.startsWith("video")
  );
  const getPublishDisabledReason = (connection: PlatformConnection) => {
    if (publishedConnectionIds.includes(connection.id)) return "Published";
    if (connection.status === "expired") return "Reconnect to publish";
    if (connection.platform === "youtube" && !hasPublishableMedia)
      return "Needs media";
    return null;
  };

  // Every channel starts ticked; people untick the ones this post skips.
  // Every eligible channel, as the modal ticks them when nothing is selected.
  const tickEveryEligibleConnection = () => {
    connections
      .filter(
        (connection) =>
          !!getPlatformConfig(connection.platform) &&
          !getPublishDisabledReason(connection)
      )
      .forEach((connection) => handleToggleConnection(connection.id, true));
  };

  const openPublishModal = (options?: {
    scheduling?: boolean;
    tickRecentlyConnected?: boolean;
    source?: string;
  }) => {
    setPublishStartScheduling(!!options?.scheduling);
    // Back from connecting an account in the modal: tick the new channel.
    if (options?.tickRecentlyConnected) {
      connections
        .filter(
          (connection) =>
            connection.createdAt > Date.now() - RECENT_CONNECT_MS &&
            !selectedConnections.has(connection.id) &&
            !getPublishDisabledReason(connection)
        )
        .forEach((connection) => handleToggleConnection(connection.id, true));
    }
    if (selectedConnections.size === 0) {
      if (connectionsLoaded) tickEveryEligibleConnection();
      // Opened before channels loaded: tick them once they arrive.
      else tickWhenConnectionsLoadRef.current = true;
    }
    setPublishFocusRequest(null);
    setIsPublishModalOpen(true);
    posthog.capture("publish_modal_opened", {
      postId: originalPost?.id ?? draftId ?? undefined,
      source: options?.source ?? "editor",
      connectedCount: connections.length,
      // Before any auto-tick: 0 means the modal ticked every eligible channel.
      preselectedCount: selectedConnections.size,
    });
  };

  useEffect(() => {
    if (!tickWhenConnectionsLoadRef.current) return;
    if (!isPublishModalOpen) {
      tickWhenConnectionsLoadRef.current = false;
      return;
    }
    if (!connectionsLoaded) return;
    tickWhenConnectionsLoadRef.current = false;
    if (selectedConnections.size === 0) tickEveryEligibleConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublishModalOpen, connectionsLoaded]);

  const existingSchedules = useMemo(
    () =>
      hasPendingWithTasks
        ? Object.fromEntries(
            Object.entries(connectionSchedules).map(([id, data]) => [
              id,
              data.scheduledFor,
            ])
          )
        : {},
    [hasPendingWithTasks, connectionSchedules]
  );

  const publishBlockingMessage = isOverConnectionLimit
    ? `Your plan includes ${maxConnections} channel${maxConnections === 1 ? "" : "s"}, and ${connections.length} are connected`
    : hasUploadingItems
    ? "Uploading media..."
    : mediaRequired && uploadItems.length === 0
      ? "Add media to publish"
      : hasCriticalMediaErrors
        ? "Fix or remove media with errors"
        : null;

  const capturePublishModalSubmit = (action: "publish_now" | "schedule") => {
    const ids = Array.from(selectedConnections);
    posthog.capture("publish_modal_submitted", {
      postId: originalPost?.id ?? draftId ?? undefined,
      action,
      channelCount: ids.length,
      connectedCount: connections.length,
      platforms: ids
        .map((id) => connections.find((c) => c.id === id)?.platform)
        .filter(Boolean),
      firstCommentCount: ids.filter(
        (id) =>
          (platformSettings[id] as FirstCommentOverrides | undefined)
            ?.firstCommentEnabled
      ).length,
    });
  };

  const handlePublishNowFromModal = () => {
    capturePublishModalSubmit("publish_now");
    setIsScheduled(false);
    setConnectionSchedules({});
    setCommonSettings((prev) => ({ ...prev, scheduledTime: "" }));
    setPublishIntent("now");
  };

  const handleScheduleFromModal = (times: Record<string, number>) => {
    capturePublishModalSubmit("schedule");
    const entries = Object.entries(times);
    setConnectionSchedules(
      Object.fromEntries(
        entries.map(([id, scheduledFor]) => [
          id,
          {
            scheduledFor,
            platform: connections.find((c) => c.id === id)!.platform,
          },
        ])
      )
    );
    const earliest = Math.min(...entries.map(([, time]) => time));
    setCommonSettings((prev) => ({
      ...prev,
      scheduledTime: formatDate(earliest, "datetime-local"),
    }));
    setIsScheduled(true);
    setHasUnsavedChanges(true);

    // A post scheduled per-connection carries no post-level scheduledFor, so
    // ask the connections too when telling first schedules from edits.
    const previouslyScheduled =
      !!originalPost?.scheduledFor ||
      Object.values(originalPost?.connections ?? {}).some(
        (conn) => !!conn.scheduledFor
      );
    posthog.capture(previouslyScheduled ? "post_rescheduled" : "post_scheduled", {
      postId: originalPost?.id,
      source: "publish_modal",
      connectionCount: entries.length,
      scheduledFor: earliest,
      oldScheduledFor: originalPost?.scheduledFor || undefined,
      conflictingSlots: entries.filter(
        ([, time]) => findConflicts(scheduleByDate, time).length > 0
      ).length,
    });
    setPublishIntent("schedule");
  };

  // Pinterest needs a board. The account list used to pick the first one as
  // soon as a channel was ticked; keep that, so a ticked Pinterest channel is
  // ready without being opened in the modal (publishing uses the same default).
  const pinterestBoardRequestsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!currentUser) return;
    for (const id of Array.from(selectedConnections)) {
      const connection = connections.find((c) => c.id === id);
      const settings = platformSettings[id] as PinterestSettings | undefined;
      if (
        connection?.platform !== "pinterest" ||
        !settings ||
        settings.boardId ||
        pinterestBoardRequestsRef.current.has(id)
      ) {
        continue;
      }
      pinterestBoardRequestsRef.current.add(id);
      void (async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE}/connections/pinterest/${id}/boards`,
            { headers: { Authorization: `Bearer ${await currentUser.getIdToken()}` } }
          );
          if (!response.ok) return;
          const data = await response.json();
          const board = Array.isArray(data.boards) ? data.boards[0] : undefined;
          if (!board) return;
          setPlatformSettings((prev) => {
            const current = prev[id] as PinterestSettings | undefined;
            if (!current || current.boardId) return prev;
            return { ...prev, [id]: { ...current, boardId: board.id, boardName: board.name } };
          });
        } catch {
          // The board picker in the modal still lets people choose.
        }
      })();
    }
  }, [selectedConnections, connections, platformSettings, currentUser]);

  // Publish once the schedule state above has rendered, so saveDraft and the
  // /schedule request read the times the modal just set.
  useEffect(() => {
    if (!publishIntent) return;
    setPublishIntent(null);
    void publishPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishIntent]);

  type RenditionPreview = React.ComponentProps<
    typeof ImageCompositeEditor
  >["renditionPreview"];

  const settingsOf = (connectionId: string) =>
    platformSettings[connectionId] as
      | (VideoSettingsOverrides & {
          postType?: string;
          title?: string;
          description?: string;
        })
      | undefined;

  // How one channel publishes this post: its native frame, format, caption.
  const buildRenditionPreview = (
    connection: PlatformConnection
  ): RenditionPreview => {
    const settings = settingsOf(connection.id);
    return {
      platform: connection.platform,
      // IG/FB "story" postType wins — the platform publishes the media AS a
      // story, whatever it is. Otherwise: converted video posts publish video
      // only where the platform supports video output; on image carousels
      // only YouTube converts to video.
      format:
        settings?.postType === "story"
          ? "story"
          : ((settings?.videoEnabled ?? hasVideoPostOutput) &&
                platformSupportsVideoPosts(connection.platform)) ||
              connection.platform === "youtube"
            ? "video"
            : "carousel",
      title: settings?.title || commonSettings.title,
      channelName: connection.profileName,
      profileImage: connection.profileImage,
      caption: getEffectivePlatformDescription(
        connection.platform,
        settings?.description,
        commonSettings.description
      ),
      videoOverrides: settings,
      onVideoOverridesChange: (patch) =>
        handleSettingsChange(connection.id, patch),
    };
  };

  const renderImageEditor = (renditionPreview?: RenditionPreview) => (
    <ImageCompositeEditor
      mediaItems={studioEditorMedia.mediaItems}
      selectedItemPreviewUrl={selectedItemPreviewUrl}
      selectedOverlayPreviewUrls={selectedOverlayPreviewUrls}
      selectedItemIndex={selectedStudioEditorIndex}
      onSelectItem={(editorIndex) => {
        const uploadIndex =
          studioEditorMedia.uploadIndexByEditorIndex[editorIndex];
        if (uploadIndex !== undefined) {
          handleSelectItem(uploadIndex);
        }
      }}
      overlays={
        uploadItems[safeSelectedIndex]?.mediaItem?.metadata
          ?.overlays || []
      }
      onOverlaysChange={handleOverlayChange}
      onBackgroundChange={handleBackgroundChange}
      backgroundColor={uploadItems[safeSelectedIndex]?.mediaItem?.metadata?.backgroundColor}
      onBackgroundColorChange={handleBackgroundColorChange}
      isBackgroundTemplateAsset={uploadItems[safeSelectedIndex]?.mediaItem?.metadata?.isBackgroundTemplateAsset}
      activeBrandKitId={originalPost?.brandKitId}
      onBackgroundTemplateAssetChange={(value) => {
        setUploadItems((current) =>
          current.map((it, idx) => {
            if (idx !== selectedIndex || !it.mediaItem) return it;
            return {
              ...it,
              mediaItem: {
                ...it.mediaItem,
                metadata: {
                  ...it.mediaItem.metadata,
                  isBackgroundTemplateAsset: value || undefined,
                },
              },
            };
          })
        );
        setHasUnsavedChanges(true);
      }}
      backgroundFilter={uploadItems[safeSelectedIndex]?.mediaItem?.metadata?.backgroundFilter}
      onBackgroundFilterChange={(value) => {
        setUploadItems((current) =>
          current.map((it, idx) => {
            if (idx !== selectedIndex || !it.mediaItem) return it;
            return {
              ...it,
              mediaItem: {
                ...it.mediaItem,
                metadata: { ...it.mediaItem.metadata, backgroundFilter: value },
              },
            };
          })
        );
        setHasUnsavedChanges(true);
      }}
      userId={activeWorkspaceId || ""}
      draftId={draftId || ""}
      extractedArticleImages={
        originalPost?.extractedArticleImages
      }
      // The publish modal's preview is read-only: no undo history, and no
      // second window-level Cmd+Z listener doubling the editor's.
      saveSnapshot={renditionPreview ? undefined : editorSaveSnapshot}
      undo={renditionPreview ? undefined : editorUndo}
      redo={renditionPreview ? undefined : editorRedo}
      canUndo={renditionPreview ? false : editorCanUndo}
      canRedo={renditionPreview ? false : editorCanRedo}
      savedVideoChoreography={originalVideoChoreography}
      savedVideoChoreographyHash={originalPost?.videoChoreographyHash}
      savedVideoMusicPath={
        "videoMusicPath" in savedVideoSettingsEdits
          ? savedVideoSettingsEdits.videoMusicPath
          : originalPost?.videoMusicPath
      }
      savedVideoAudioMuted={
        savedVideoSettingsEdits.videoAudioMuted ?? originalPost?.videoAudioMuted
      }
      savedVideoSpeed={savedVideoSettingsEdits.videoSpeed ?? originalPost?.videoSpeed}
      onVideoChoreographyChange={setLiveVideoChoreography}
      onVideoMusicResolved={(musicPath) =>
        setSavedVideoSettingsEdits((prev) => ({ ...prev, videoMusicPath: musicPath }))
      }
      onPersistVideoSettings={persistVideoSettings}
      onBeforeVideoRevert={settlePendingVideoSettingsSave}
      onVideoReverted={clearVideoSettingsSaveQueue}
      onVideoMutationPendingChange={
        setIsEditorVideoMutationPending
      }
      videoSettingsDisabled={areVideoSettingsLocked}
      onBeforeVideoConvert={async () => {
        await prepareMediaForProcessing();
      }}
      renditionPreview={renditionPreview}
      onBackgroundImageResolved={(url) => {
        setUploadItems((current) =>
          current.map((it, idx) =>
            idx === selectedIndex && (it.error || (it.uploadProgress !== undefined && it.uploadProgress < 100))
              ? { ...it, preview: url, error: undefined, uploadProgress: 100 }
              : it
          )
        );
      }}
      bottomSlot={
        renditionPreview ? undefined : (
          <div className="h-28 border-t bg-card px-4 py-2 shrink-0 flex items-center overflow-x-auto">
            <MediaFilmstrip
              items={uploadItems}
              selectedIndex={selectedIndex}
              onSelect={setSelectedIndex}
              onReorder={reorderUploadItems}
              onRemove={removeUploadItem}
              onAdd={(e) => handleMediaUpload(e)}
              onDuplicate={handleDuplicateMediaItem}
            />
          </div>
        )
      }
    />
  );

  // The publish modal's preview of one channel.
  const renderChannelPreview = (connection: PlatformConnection) => {
    const item = uploadItems[safeSelectedIndex];
    if (uploadItems.length === 0) {
      return (
        <div className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground">
          This post has no media yet.
        </div>
      );
    }
    if (uploadItems.every((it) => it.type?.startsWith("image"))) {
      return selectedStudioEditorIndex === -1 ? (
        <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Preparing media...
        </div>
      ) : (
        renderImageEditor(buildRenditionPreview(connection))
      );
    }
    if (item?.mediaItem) {
      const settings = settingsOf(connection.id);
      return (
        <UploadedVideoRenditionPreview
          key={connection.id}
          platform={connection.platform}
          format={settings?.postType === "story" ? "story" : "video"}
          channelName={connection.profileName}
          profileImage={connection.profileImage}
          caption={getEffectivePlatformDescription(
            connection.platform,
            settings?.description,
            commonSettings.description
          )}
          title={settings?.title || commonSettings.title}
          videoSrc={item.preview}
        />
      );
    }
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Uploading video...
      </div>
    );
  };

  if (isLoading) {
  return (
      <div className="flex flex-col h-screen w-full bg-background overflow-hidden fixed inset-0 z-0">
        <div className="h-14 border-b flex items-center justify-between px-4 bg-card shrink-0 z-20">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded bg-muted animate-pulse" />
            <div className="h-6 w-20 rounded bg-muted animate-pulse" />
            <div className="w-px h-6 bg-border hidden md:block" />
            <div className="h-4 w-40 rounded bg-muted animate-pulse hidden md:block" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-20 rounded bg-muted animate-pulse hidden md:block" />
            <div className="h-8 w-8 rounded bg-muted animate-pulse" />
            <div className="h-8 w-8 rounded bg-muted animate-pulse" />
            <div className="h-8 w-20 rounded bg-muted animate-pulse" />
          </div>
        </div>

        {isMobile ? (
          /* Mobile loading skeleton */
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="w-full h-[50vh] bg-muted animate-pulse" />
            <div className="h-24 border-t bg-card px-4 py-2 flex items-center gap-2">
              <div className="h-16 w-12 rounded bg-muted animate-pulse" />
              <div className="h-16 w-12 rounded bg-muted animate-pulse" />
              <div className="h-16 w-12 rounded bg-muted animate-pulse" />
            </div>
            <div className="p-4 space-y-4">
              <div className="h-5 w-32 rounded bg-muted animate-pulse" />
              <div className="h-10 w-full rounded bg-muted animate-pulse" />
              <div className="h-32 w-full rounded bg-muted animate-pulse" />
            </div>
          </div>
        ) : (
          /* Desktop loading skeleton */
          <div className="flex-1 flex min-h-0">
            <div className="w-80 border-r flex-shrink-0 bg-card h-full overflow-hidden p-4 space-y-4">
              <div className="h-5 w-32 rounded bg-muted animate-pulse" />
              <div className="h-10 w-full rounded bg-muted animate-pulse" />
              <div className="h-32 w-full rounded bg-muted animate-pulse" />
              <div className="h-6 w-24 rounded bg-muted animate-pulse" />
              <div className="h-16 w-full rounded bg-muted animate-pulse" />
              <div className="h-16 w-full rounded bg-muted animate-pulse" />
            </div>

            <div className="flex-1 min-w-0 h-full overflow-hidden flex flex-col">
              <div className="flex-1 flex items-center justify-center bg-muted/20 p-6">
                <div className="aspect-[9/16] h-full max-h-full w-full max-w-[420px] rounded bg-muted animate-pulse" />
              </div>
              <div className="h-28 border-t bg-card px-4 py-2 flex items-center gap-2">
                <div className="h-20 w-14 rounded bg-muted animate-pulse" />
                <div className="h-20 w-14 rounded bg-muted animate-pulse" />
                <div className="h-20 w-14 rounded bg-muted animate-pulse" />
              </div>
            </div>
          </div>
        )}

        <div className="absolute top-16 right-4 text-xs text-muted-foreground">
          {isCloningFromShare
            ? "Saving post to your account..."
            : "Loading Studio..."}
        </div>
      </div>
    );
  }

  return (
      <div className="flex flex-col h-screen w-full bg-background overflow-hidden fixed inset-0 z-0">
        {/* Top Bar (full width) */}
        <div className="h-14 border-b flex items-center justify-between px-4 bg-card shrink-0 z-20">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => dashboardSidebar?.openSidebar()}>
              <Menu className="h-4 w-4" />
            </Button>
            <img src="/icon.svg" alt="WaveGen Logo" className="h-6 w-auto" />
            <div className="w-px h-6 bg-border hidden md:block" />
            <h1
              className={cn(
                commonSettings.title && "notranslate",
                "font-semibold text-sm truncate max-w-[200px] hidden md:block"
              )}
              translate={commonSettings.title ? "no" : undefined}
            >
              {commonSettings.title || "Untitled Post"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Auto-save status indicator */}
            <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
              {isSaving ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : hasUnsavedChanges ? (
                <>
                  <div className="h-2 w-2 rounded-full bg-yellow-500" />
                  <span>Unsaved</span>
                </>
              ) : (
                <>
                  <Check className="h-3 w-3 text-green-600" />
                  <span className="text-green-600">Saved</span>
                </>
              )}
            </div>

            {/* Share Button */}
            <DropdownMenu
              onOpenChange={(open) => {
                if (open) {
                  posthog.capture("share_panel_opened", {
                    share_enabled: shareEnabled,
                  });
                }
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" disabled={shareLoading || !draftId}>
                  {shareLoading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Share2 className={cn("h-4 w-4", shareEnabled && "text-orange-500")} />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-3" onClick={(e) => e.preventDefault()}>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="share-toggle" className="text-sm font-medium cursor-pointer">
                      Public link
                    </Label>
                    <Switch
                      id="share-toggle"
                      checked={shareEnabled}
                      disabled={shareLoading}
                      onCheckedChange={(checked) => checked ? handleEnableShare() : handleDisableShare()}
                    />
                  </div>
                  {!shareEnabled && (
                    <p className="text-xs text-muted-foreground">
                      Enable to create a public link anyone can view
                    </p>
                  )}
                  {shareEnabled && shareId && (
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={`${window.location.origin}/share/${shareId}`}
                        className="text-xs h-8"
                        onClick={(e) => e.currentTarget.select()}
                      />
                      <Button size="sm" variant="outline" className="shrink-0 h-8" onClick={handleCopyShareLink}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Export Button */}
            {allMediaItemsAreImages && !liveVideoChoreography ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={isMobile ? "ghost" : "outline"}
                    size="sm"
                    disabled={isExporting || hasUploadingItems || uploadItems.length === 0}
                  >
                    {isExporting ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Download className={isMobile ? "h-4 w-4" : "h-3 w-3"} />
                    )}
                    <span className="hidden md:inline ml-1">Export</span>
                    {exportShowsUpgrade && (
                      <span className="hidden md:inline-flex items-center rounded-full bg-[#FF5C35]/10 p-1 ml-1.5 text-[#FF5C35]">
                        <Crown className="h-2.5 w-2.5" />
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportMedia("individual")} disabled={isExporting}>
                    <Download className="mr-2 h-4 w-4" />
                    Export as Images
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportMedia("pdf")} disabled={isExporting}>
                    <Download className="mr-2 h-4 w-4" />
                    Export as PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant={isMobile ? "ghost" : "outline"}
                size="sm"
                onClick={() => exportMedia("individual")}
                disabled={isExporting || hasUploadingItems || uploadItems.length === 0}
              >
                {isExporting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Download className={isMobile ? "h-4 w-4" : "h-3 w-3"} />
                )}
                <span className="hidden md:inline ml-1">Export</span>
                {exportShowsUpgrade && (
                  <span className="hidden md:inline-flex items-center rounded-full bg-[#FF5C35]/10 p-1 ml-1.5 text-[#FF5C35]">
                    <Crown className="h-2.5 w-2.5" />
                  </span>
                )}
              </Button>
            )}

            {/* Already scheduled: when, and a way to unschedule */}
            {hasPendingWithTasks && scheduleSummaryLabel && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-orange-700 border-orange-200 bg-orange-50 hover:bg-orange-100"
                onClick={() => openPublishModal()}
              >
                <CalendarDays className="h-3 w-3" />
                <span className="max-w-[80px] md:max-w-[160px] truncate">{scheduleSummaryLabel}</span>
                <button
                  className="ml-0.5 rounded-full p-0.5 hover:bg-orange-200 transition-colors"
                  aria-label="Unschedule"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCancelConfirmOpen(true);
                  }}
                >
                  <X className="h-3 w-3" />
                </button>
              </Button>
            )}

            {/* One entry point: channels, previews and timing live in the modal */}
            <Button
              size="sm"
              className="bg-[#FF5C35] hover:bg-[#E5502F] text-white"
              onClick={() => openPublishModal()}
              aria-label="Preview & publish"
              disabled={
                isPublishing ||
                isExporting ||
                isVideoMutationPending
              }
            >
              {isPublishing || isExporting ? (
                <Loader2 className="h-3 w-3 animate-spin md:mr-1" />
              ) : (
                <Send className="h-3 w-3 md:mr-1" />
              )}
              <span className="hidden md:inline">Preview & publish</span>
            </Button>


          </div>
        </div>

        {mediaUnreachable && (
          <Alert
            variant="destructive"
            className="rounded-none border-x-0 border-t-0 py-2 shrink-0"
          >
            <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>
                Your slides can't load because your browser can't reach our
                servers. Check your connection, or pause ad blockers and privacy
                extensions for this site, then reload.
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-7"
                onClick={() => window.location.reload()}
              >
                Reload
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Body */}
        {isMobile ? (
          /* Mobile: single-column scrollable layout */
          <div className="flex-1 overflow-y-auto min-h-0">
            {uploadItems.length === 0 ? (
              <div className="p-4">
                <div className="bg-card p-6 rounded-lg shadow-sm border">
                  <h3 className="text-lg font-semibold mb-4 text-center">Add Media</h3>
                  <MediaUploadSection
                    uploadItems={uploadItems}
                    handleMediaUpload={handleMediaUpload}
                    removeUploadItem={removeUploadItem}
                    reorderUploadItems={reorderUploadItems}
                    selectedIndex={selectedIndex}
                    onSelectItem={handleSelectItem}
                    onReplaceItem={handleReplaceMediaItem}
                    onDuplicateItem={handleDuplicateMediaItem}
                    onEditItem={openMediaEditor}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col">
                {canConvertCarouselToVideo && (
                  <div className="flex flex-col items-center gap-2 border-y bg-card px-4 py-3">
                    <PostFormatToggle label="Default format" mediaCount={uploadItems.length}
                      loading={isLoadingChoreography} video={showVideoPreview}
                      onChange={(video) => { if (video !== showVideoPreview) void (video ? handleMobileConvertToVideo() : handleMobileRevertToCarousel()); }}
                      disabled={isVideoMutationPending || isSaving || isPublishing || isExporting} />
                    <p className="text-xs text-muted-foreground">You can choose a different format for each platform.</p>
                  </div>
                )}
                {/* Video post: show video player + settings */}
                {showVideoPreview && speedAdjustedChoreography && videoPreviewSlides.length >= 1 ? (
                  <>
                  <div className="w-full bg-black flex items-center justify-center">
                    <div className="w-full" style={mobileVideoPreviewStyle}>
                      <CarouselVideoPreview
                        key={`playing-${videoSpeed}`}
                        slides={videoPreviewSlides}
                        choreography={speedAdjustedChoreography}
                        containerWidth={videoPreviewSlides[0]?.dimensions.width ?? 1080}
                        containerHeight={videoPreviewSlides[0]?.dimensions.height ?? 1350}
                        layoutEngine="remotion"
                        musicUrl={videoAudioMuted ? undefined : videoMusicUrl}
                        style={{ width: '100%', height: '100%' }}
                      />
                    </div>
                  </div>

                  {/* Video Settings (mobile, collapsible summary bar) */}
                  <Collapsible className="border-b bg-card">
                    <CollapsibleTrigger className="group flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Music className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">
                          {videoAudioMuted
                            ? "Muted"
                            : videoMusicPath
                            ? videoMusicPath.split("/").pop()?.replace(/[-_]/g, " ").replace(/\.\w+$/, "")
                            : "No music"}
                        </span>
                      </div>
                      <span className="text-xs font-medium tabular-nums text-muted-foreground shrink-0">
                        {videoSpeed}x
                      </span>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="px-4 pb-3 space-y-3">
                      <Separator />
                      <VideoAudioSettings
                        postId={draftId ?? undefined}
                        musicPath={videoMusicPath}
                        audioMuted={videoAudioMuted}
                        speed={videoSpeed}
                        onMusicChange={(path) => setVideoMusicPath(path)}
                        onMuteChange={setVideoAudioMuted}
                        onSpeedChange={setVideoSpeed}
                        onPersistSettings={persistVideoSettings}
                        disabled={areVideoSettingsLocked}
                        compact
                      />
                    </CollapsibleContent>
                  </Collapsible>
                  <div className="mx-4 mt-3 flex items-center gap-2 rounded-md bg-muted px-3 py-2">
                    <Info className="h-4 w-4 text-muted-foreground shrink-0" />
                    <p className="text-xs text-muted-foreground">
                      Slide editing is available on desktop
                    </p>
                  </div>
                  </>
                ) : (
                <>
                {/* Carousel view-only preview */}
                <div className={cn(
                  "w-full bg-black",
                  uploadItems.length > 1
                    ? "grid grid-cols-[44px_minmax(0,1fr)_44px] items-center"
                    : "flex items-center justify-center"
                )}>
                  {uploadItems.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-11 w-11 text-white hover:bg-white/10 hover:text-white focus-visible:ring-inset"
                      aria-label="Previous slide"
                      disabled={safeSelectedIndex === 0}
                      onClick={() => handleSelectItem(safeSelectedIndex - 1)}
                    >
                      <ChevronLeft className="h-6 w-6" aria-hidden="true" />
                    </Button>
                  )}
                  <div className="min-w-0 w-full flex items-center justify-center">
                  {uploadItems[safeSelectedIndex]?.type?.startsWith("image") ? (
                    <div className="w-full" style={mobileCarouselPreviewStyle}>
                      <ScenePreviewCanvasWrapper
                        backgroundImagePath={uploadItems[safeSelectedIndex]?.mediaItem?.path}
                        backgroundImageUrl={selectedItemPreviewUrl}
                        backgroundFilter={uploadItems[safeSelectedIndex]?.mediaItem?.metadata?.backgroundFilter}
                        overlays={uploadItems[safeSelectedIndex]?.mediaItem?.metadata?.overlays || []}
                        overlayPreviewUrls={selectedOverlayPreviewUrls}
                        referenceMediaWidth={uploadItems[safeSelectedIndex]?.mediaItem?.metadata?.dimensions?.width}
                        onBackgroundImageResolved={(url) => {
                          setUploadItems((current) =>
                            current.map((it, idx) =>
                              idx === selectedIndex && (it.error || (it.uploadProgress !== undefined && it.uploadProgress < 100))
                                ? { ...it, preview: url, error: undefined, uploadProgress: 100 }
                                : it
                            )
                          );
                        }}
                      />
                    </div>
                  ) : uploadItems[safeSelectedIndex]?.mediaItem ? (
                    <div className="w-full h-[50vh] flex items-center justify-center bg-black">
                      <SimpleVideoPlayer src={uploadItems[safeSelectedIndex].preview} />
                    </div>
                  ) : (
                    <div className="w-full h-[50vh] flex items-center justify-center bg-black">
                      <div className="flex items-center gap-2 text-sm text-white/60">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Uploading video...
                      </div>
                    </div>
                  )}
                  </div>
                  {uploadItems.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-11 w-11 text-white hover:bg-white/10 hover:text-white focus-visible:ring-inset"
                      aria-label="Next slide"
                      disabled={safeSelectedIndex === uploadItems.length - 1}
                      onClick={() => handleSelectItem(safeSelectedIndex + 1)}
                    >
                      <ChevronRight className="h-6 w-6" aria-hidden="true" />
                    </Button>
                  )}
                </div>
                {uploadItems.length > 1 && (
                  <p className="py-2 text-center text-xs tabular-nums text-muted-foreground" aria-live="polite" aria-atomic="true">
                    Slide {safeSelectedIndex + 1} of {uploadItems.length}
                  </p>
                )}

                {/* Desktop-only editing banner (images only) */}
                {uploadItems[safeSelectedIndex]?.type?.startsWith("image") && (
                  <div className="mx-4 mt-3 flex items-center gap-2 rounded-md bg-muted px-3 py-2">
                    <Info className="h-4 w-4 text-muted-foreground shrink-0" />
                    <p className="text-xs text-muted-foreground">
                      Media editing is available on desktop
                    </p>
                  </div>
                )}
                </>
                )}
              </div>
            )}

            {/* Post Settings (inline, not in sidebar) */}
            <div className="p-4">
              <PostSettingsPanel
                title={commonSettings.title}
                description={commonSettings.description}
                onTitleChange={(val) => handleCommonSettingsChange("title", val)}
                onDescriptionChange={(val) =>
                  handleCommonSettingsChange("description", val)
                }
                firstComment={commonSettings.firstComment}
                onFirstCommentChange={(val) => handleCommonSettingsChange("firstComment", val)}
                defaultFirstComment={defaultFirstComment}
              />
            </div>
          </div>
        ) : (
          /* Desktop: Left Sidebar + Center Stage */
          <div className="flex-1 flex min-h-0">
            {/* Left Sidebar */}
            <div
              className={cn(
                "border-r flex-shrink-0 bg-card z-10 h-full overflow-hidden",
                "w-96"
              )}
            >
              <PostSettingsPanel
                title={commonSettings.title}
                description={commonSettings.description}
                onTitleChange={(val) => handleCommonSettingsChange("title", val)}
                onDescriptionChange={(val) =>
                  handleCommonSettingsChange("description", val)
                }
                firstComment={commonSettings.firstComment}
                onFirstCommentChange={(val) => handleCommonSettingsChange("firstComment", val)}
                defaultFirstComment={defaultFirstComment}
              />
            </div>

            {/* Center Stage + Right Sidebar */}
            <div className="flex-1 min-w-0 h-full overflow-hidden">
              {uploadItems.length === 0 ? (
                <div className="flex h-full items-center justify-center p-8">
                  <div className="bg-card p-8 rounded-lg shadow-sm border max-w-lg w-full">
                    <h3 className="text-lg font-semibold mb-4 text-center">Add Media</h3>
                    <MediaUploadSection
                      uploadItems={uploadItems}
                      handleMediaUpload={handleMediaUpload}
                      removeUploadItem={removeUploadItem}
                      reorderUploadItems={reorderUploadItems}
                      selectedIndex={selectedIndex}
                      onSelectItem={handleSelectItem}
                      onReplaceItem={handleReplaceMediaItem}
                      onDuplicateItem={handleDuplicateMediaItem}
                      onEditItem={openMediaEditor}
                    />
                  </div>
                </div>
              ) : uploadItems[safeSelectedIndex]?.type?.startsWith("image") ? (
                selectedStudioEditorIndex === -1 ? (
                  <div className="flex h-full items-center justify-center bg-muted/20">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Preparing media...
                    </div>
                  </div>
                ) : (
                  renderImageEditor()
                )
              ) : uploadItems[safeSelectedIndex]?.mediaItem ? (
                  /* Video: simple playback, no editor or filmstrip */
                  <div className="flex items-center justify-center bg-black h-full">
                    <SimpleVideoPlayer src={uploadItems[safeSelectedIndex].preview} />
                  </div>
              ) : (
                <div className="flex items-center justify-center bg-black h-full">
                  <div className="flex items-center gap-2 text-sm text-white/60">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading video...
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* First-publish YouTube conversion nudge */}
        <Dialog open={youtubeNudgeOpen} onOpenChange={setYoutubeNudgeOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>YouTube gets a video slideshow</DialogTitle>
              <DialogDescription>
                Your slides publish there as an auto-generated vertical video.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setYoutubeNudgeOpen(false);
                  setPendingAction(null);
                  const youtubeId = Array.from(selectedConnections).find(
                    (id) =>
                      connections.find((c) => c.id === id)?.platform ===
                      "youtube"
                  );
                  if (youtubeId) {
                    setPublishFocusRequest({
                      connectionId: youtubeId,
                      nonce: Date.now(),
                    });
                    setPublishStartScheduling(false);
                    setIsPublishModalOpen(true);
                  }
                }}
              >
                See it first
              </Button>
              <Button
                onClick={() => {
                  setYoutubeNudgeOpen(false);
                  pendingAction?.();
                  setPendingAction(null);
                }}
              >
                Publish
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Watermark Confirmation Dialog */}
        <WatermarkConfirmDialog
          isOpen={isWatermarkDialogOpen}
          generationQualityVariant={originalPost?.generationQualityVariant}
          postId={originalPost?.id}
          onClose={() => {
            setIsWatermarkDialogOpen(false);
            setPendingAction(null);
          }}
          onProceed={() => {
            if (pendingAction) {
              pendingAction();
            }
          }}
          onUpgrade={async () => {
            setIsWatermarkDialogOpen(false);
            setPendingAction(null);
            const upgradeReason =
              EXPORT_GATE_ENABLED && watermarkDialogAction === "export"
                ? "export_gate"
                : "watermark";
            // Capture BEFORE the await below. Navigation is gated on a draft
            // save, so if that save stalls the user never reaches /upgrade —
            // and without this event a click that went nowhere is
            // indistinguishable from no click at all. Pair this against
            // `/upgrade?reason=...` arrivals to see the drop-off directly.
            posthog.capture("upgrade_cta_clicked", {
              reason: upgradeReason,
              surface: "watermark_dialog",
              had_unsaved_changes: hasUnsavedChanges,
              generation_quality_variant:
                originalPost?.generationQualityVariant,
              postId: originalPost?.id,
            });
            // Persist first so both the Back button and the post-checkout
            // successUrl return to this exact draft, not a blank editor.
            // handleSave returns the (possibly newly created) draftId and the
            // effect above stamps it into the URL.
            const returnDraftId =
              hasUnsavedChanges || !draftId
                ? await handleSave({ silent: true })
                : draftId;
            navigateToUpgrade({
              reason: upgradeReason,
              successUrl: returnDraftId
                ? `${window.location.origin}/create-post?draftId=${returnDraftId}`
                : undefined,
            });
          }}
          action={watermarkDialogAction}
          gated={EXPORT_GATE_ENABLED}
          onPublishInstead={() => setIsPublishModalOpen(true)}
        />

        {/* Export Loading Modal */}
        <Dialog open={isExportLoadingModalOpen} onOpenChange={() => {}}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Processing Media for Export</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-8 space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <div className="text-center space-y-2">
                <p className="text-sm font-medium">
                  Processing your media...
                </p>
                <p className="text-xs text-muted-foreground">
                  This may take a few moments while we apply overlays, audio,
                  and effects to your content.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Export Results Modal */}
        <Dialog open={isExportModalOpen} onOpenChange={setIsExportModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Export Complete
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Export successful
                  {getExportDownloadLabel(exportResults, exportTitle) ? (
                    <>
                      {" "}
                      as{" "}
                      <span className="font-medium text-foreground">
                        {getExportDownloadLabel(exportResults, exportTitle)}
                      </span>
                    </>
                  ) : null}
                  . Check your downloads folder.
                </p>
                {!isMultiImageZipExportResult(exportResults) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void handleDownloadExportAgain()}
                  >
                    <Download className="mr-1 h-3 w-3" />
                    Download Again
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <PublishModal
          open={isPublishModalOpen}
          onOpenChange={setIsPublishModalOpen}
          connections={connections}
          connectionsStatus={connectionsStatus}
          onRetryConnections={() => setConnectionsReloadNonce((n) => n + 1)}
          selectedConnectionIds={selectedConnections}
          onToggleConnection={handleToggleConnection}
          getDisabledReason={getPublishDisabledReason}
          publishedAtById={publishedAtById}
          focusRequest={publishFocusRequest}
          platformSettings={platformSettings}
          onSettingsChange={handleSettingsChange}
          mediaItems={compact(uploadItems.map((item) => item.mediaItem))}
          isVideoPost={hasVideoPostOutput}
          postTitle={commonSettings.title}
          postDescription={commonSettings.description}
          postFirstComment={commonSettings.firstComment || defaultFirstComment}
          defaultPinterestLink={defaultPinterestLink}
          pinterestDescriptionGeneratingIds={pinterestDescriptionGeneratingIds}
          renderPreview={renderChannelPreview}
          scheduleByDate={scheduleByDate}
          connectionsById={connectionsById}
          existingSchedules={existingSchedules}
          preferredTime={
            !hasPendingWithTasks && commonSettings.scheduledTime
              ? new Date(commonSettings.scheduledTime).getTime()
              : undefined
          }
          timezone={targetTimezone}
          timezoneLabel={timezoneLabel}
          blockingMessage={publishBlockingMessage}
          blockingAction={
            isOverConnectionLimit
              ? { label: "Manage channels", onClick: () => navigate("/settings") }
              : undefined
          }
          isPublishing={isPublishing}
          onPublishNow={handlePublishNowFromModal}
          onSchedule={handleScheduleFromModal}
          startScheduling={publishStartScheduling}
          onConnectPlatform={handleConnectFromPost}
          atConnectionLimit={connections.length >= maxConnections}
          maxConnections={maxConnections}
          onUpgradeConnections={() =>
            navigateToUpgrade({ reason: "connection_limit" })
          }
        />

        {/* Cancel Schedule Confirmation Dialog */}
        <AlreadyPublishedDialog
          notice={alreadyPublishedNotice}
          onStay={() => setAlreadyPublishedNotice(null)}
          onLeave={(path) => {
            setAlreadyPublishedNotice(null);
            // Let the dialog release <body> before the route changes.
            requestAnimationFrame(() => requestAnimationFrame(() => navigate(path)));
          }}
        />

        <Dialog open={isCancelConfirmOpen} onOpenChange={setIsCancelConfirmOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Cancel Schedule</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground py-2">
              This will cancel the scheduled post and revert it to draft status.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCancelConfirmOpen(false)}>
                Keep Scheduled
              </Button>
              <Button
                variant="destructive"
                disabled={isCancelingSchedule}
                onClick={() => {
                  setIsCancelConfirmOpen(false);
                  cancelSchedule();
                }}
              >
                {isCancelingSchedule ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Canceling...
                  </>
                ) : (
                  "Cancel Schedule"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );

}
